import { CdkDrag, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AumicoCoreLayoutProvider, Constants, DateLocalePipe } from '@modeso/aumico-lib-core-fe';
import {
    AnnualReportStep,
    IAccount,
    IAccountMappingTemplateGroup,
    IAnnualReport,
    IFinStatements,
    IGroupedAccount,
    IReclassificationAccountRef,
    IReclassificationTransaction,
    ItemType,
    ReclassificationPeriod,
    ReclassificationStatement,
} from '@modeso/types__aumico-lib-annualreport-be';
import {
    IGroupedAccountDefition,
    IGroupingMasking,
    ISplitGroupAccount,
} from '@modeso/types__aumico-lib-annualreport-be/dist/models/interfaces/IGroupingMasking';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal';
import { combineLatest, Observable, of } from 'rxjs';
import { debounceTime, filter, find, map, tap } from 'rxjs/operators';
import { NavigationItem } from '../../../../../../../aumico-components/src/lib';
import {
    findConflicts,
    getIncomingTransactions,
    getOutgoingTransactions,
    getReclassSumForRef,
    groupConflictsByAccount,
} from '../../../../../../../aumico-lib-annualreport-fe/src/lib/helpers/reclassificationEngine';
import { DueDates } from '../../../../../../../aumico-lib-annualreport-fe/src/lib/interfaces/dueDate.interface';
import { AnnualReportProvider } from '../../../../../../../aumico-lib-annualreport-fe/src/lib/provider/annualReport.provider';
import { ReclassificationStoreService } from '../../../../../../../aumico-lib-annualreport-fe/src/lib/services/reclassificationStore.service';
import {
    onUpdateAnnualReportFailed,
    onUpdateAnnualReportSuccessfully,
} from '../../../../../../../aumico-lib-annualreport-fe/src/public-api';
import { MixpanelService } from '../../../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../../../mixpanel/mixpanelEvents.enum';
import { Paths } from '../../../../enums/paths.enum';
import { AnnualReportSubPage } from '../../annual-report-sub-page';
import { isReclassNewWorld } from '../../helpers/newReclassesGate.helper';
import { GroupingHelper, INewReclassesAccountSessionEntry } from '../../utils/GroupingHelper';
import { GroupingMaskingHelper } from '../../utils/GroupingMaskingHelper';
import { GroupingPathHelper } from '../../utils/GroupingPathHelper';
import { MaskingHelper } from '../../utils/MaskingHelper';
import { SplitAccountHelper } from '../../utils/SplitAccountHelper';
import {
    SYSTEM_RECLASS_ACCOUNT_NAME_CONNECTOR,
    displayAccountNumber,
    generateSystemReclassAccountNumber,
    matchesSystemReclassAccountName,
    systemReclassAccountName,
} from '../../../../../../../aumico-lib-annualreport-fe/src/lib/helpers/reclassAccountNumber';
import { refsEqual } from '../../../../../../../aumico-lib-annualreport-fe/src/lib/helpers/reclassificationAccountKey';
import { ReclassNewTargetKind } from './reclass-new-target-choice/reclass-new-target-choice.component';
import {
    ReclassModalSavePayload,
    ReclassTargetKind,
    ReclassTargetOption,
    ReclassTargetSelection,
    targetSelectionKey,
} from './reclassification-modal/reclassification-modal.component';

const debug = Debug('aumico:saas:GroupingMaskingComponent');

// IndexedDB Constants (Only for Collapse State)
const DB_NAME = 'AnnualReportGroupingDB';
const DB_VERSION = 1;
const STORE_NAME = 'GroupingMaskingState';

@UntilDestroy()
@Component({
    selector: 'aumico-grouping-masking-component',
    templateUrl: 'grouping-masking.component.html',
    styleUrls: ['grouping-masking.component.scss'],
    providers: [ReclassificationStoreService],
})
export class GroupingMaskingComponent extends AnnualReportSubPage implements OnInit {
    // Expose the Enum to the HTML template
    public ItemType = ItemType;

    isPartOfArNavigation$: Observable<boolean>;
    readonly uploadStepLabel = Constants.ACTION_BAR.LABELS.ACCOUNT_MAPPING_KEY;
    readonly uploadStepPath = Paths.MAPPING;
    readonly mappingStepLabel = Constants.ACTION_BAR.LABELS.ACCOUNT_MAPPING_KEY;
    readonly mappingStepPath = Paths.MAPPING;
    public navigationItems: NavigationItem[] = [
        { name: 'aumico_annual_report_import_form_balance_title', active: true },
        { name: 'aumico_annual_report_import_form_income_title', active: false },
    ];
    public tabsTitles: string[] = ['Balance Sheet', 'Income Statement'];
    public selectedTab = 0;

    // FIX #1: Store full Date objects, not just years
    public dueDateCY: Date | undefined = undefined;
    public dueDatePY: Date | undefined | '-' = undefined;
    public periodCY: string | undefined = undefined;
    public periodPY: string | undefined | '-' = undefined;
    public currency: string | undefined = undefined;

    // Property to store the current Annual Report ID
    public annualReportId: string | undefined = undefined;
    public annualReport: IAnnualReport | undefined = undefined;
    finStatements: IFinStatements[];

    // View Models (Deep clones of the data for rendering)
    public balanceSheet: IAccountMappingTemplateGroup | undefined = undefined;
    public incomeStatement: IAccountMappingTemplateGroup | undefined = undefined;

    // Separate Layer for Sorting: Map<ContainerPath, AccountNumber[]>
    public sortingState: { [containerId: string]: string[] } = {};

    // Collapse State: Set of collapsed group paths
    public collapseState: Set<string> = new Set();

    // IndexedDB instance (retained for collapse state)
    private db: IDBDatabase | null = null;

    // Data to be saved
    groupingAndMaskingToBeSaved: IGroupingMasking;
    isSaveRequestInProgress: boolean = false;

    // Original data for comparison (to detect changes)
    private originalGroupingAndMasking: IGroupingMasking | null = null;

    // Bulk Edit
    public selectedAccounts: Map<string, IAccount | IGroupedAccount> = new Map<string, IAccount | IGroupedAccount>();
    public selectedSubtotals: Map<string, any> = new Map<string, any>();
    public allAccountsSelected: boolean = false;

    private _cachedBulkActions: { id: string; translationKey: string }[] = [];
    private _lastAccountsSize: number = 0;
    private _lastSubtotalsSize: number = 0;
    private _lastHiddenCount: number = 0;

    openAllFoldersWasClicked: boolean = false;

    // Sort menu state
    public isSortMenuOpen: boolean = false;
    public sortMenuPosition: { top: number; right: number; left: number } = { top: 0, right: 0, left: 0 };

    public get bulkActions(): { id: string; translationKey: string }[] {
        // Check if we need to recalculate (only when selection changes)
        const accountsChanged = this._lastAccountsSize !== this.selectedAccounts.size;
        const subtotalsChanged = this._lastSubtotalsSize !== this.selectedSubtotals.size;
        const hiddenChanged = this._lastHiddenCount !== this.hiddenSubtotalsSet.size;

        if (!accountsChanged && !subtotalsChanged && !hiddenChanged) {
            return this._cachedBulkActions;
        }

        // Update cache tracking
        this._lastAccountsSize = this.selectedAccounts.size;
        this._lastSubtotalsSize = this.selectedSubtotals.size;
        this._lastHiddenCount = this.hiddenSubtotalsSet.size;

        // Recalculate actions
        // If only subtotals selected
        if (this.selectedSubtotals.size > 0 && this.selectedAccounts.size === 0) {
            const selectedKeys = Array.from(this.selectedSubtotals.keys());
            const allHidden = selectedKeys.every((id) => this.hiddenSubtotalsSet.has(id));
            const allVisible = selectedKeys.every((id) => !this.hiddenSubtotalsSet.has(id));

            const actions = [];

            if (!allHidden) {
                actions.push({ id: 'HIDE', translationKey: 'aumico_grouping_masking_bulk_action_hide' });
            }
            if (!allVisible) {
                actions.push({ id: 'UNHIDE', translationKey: 'aumico_grouping_masking_bulk_action_unhide' });
            }

            this._cachedBulkActions = actions;
            return actions;
        }

        // If only accounts selected - existing actions
        if (this.selectedAccounts.size > 0 && this.selectedSubtotals.size === 0) {
            this._cachedBulkActions = [
                { id: 'GROUP', translationKey: 'aumico_grouping_masking_bulk_action_group' },
                { id: 'RENAME', translationKey: 'aumico_grouping_masking_bulk_action_rename' },
            ];
            return this._cachedBulkActions;
        }

        // Mixed selection or nothing selected (falls through below)
        this._cachedBulkActions = [];
        return [];
    }

    /**
     * REV-21798: the destructive action in the bulk bar.
     *
     * Only manually created (reclassification) accounts may be deleted. When
     * the selection also contains imported accounts the button is rendered but
     * disabled, so the user can see the action exists rather than wondering
     * where it went.
     *
     * Deliberately NOT part of the `bulkActions` cache: that cache keys off
     * selection *size*, while this action's enabled state depends on *which*
     * accounts are selected. Deselecting one account and selecting another
     * keeps the size identical and would serve a stale flag.
     */
    public get bulkDeleteAction(): { id: string; translationKey: string; disabled: boolean } | null {
        if (this.selectedAccounts.size === 0 || this.selectedSubtotals.size > 0) return null;
        const selected = Array.from(this.selectedAccounts.values());
        const allDeletable = selected.every((acc) => this.isReclassAccountRow(acc as any));
        return {
            id: 'DELETE',
            translationKey: 'aumico_grouping_masking_bulk_action_delete',
            disabled: !allDeletable,
        };
    }

    // Rename Modal
    public openRenameModal = false;
    public accountsToRename: any[] = [];
    public renameForm: FormGroup;
    public editRenameModal: boolean = false;

    // Group Modal
    public openGroupModal = false;
    public groupModalEditId: string | undefined = undefined;
    public accountsToGroup: any[] = [];
    public groupForm: FormGroup;
    public groupedAccountsMap: Record<string, IGroupedAccountDefition> = {};

    // Split Modal
    public openSplitModal = false;
    public splitModalEditId: string | undefined = undefined;
    public accountToSplit: IGroupedAccount | undefined = undefined;
    public splitForm: FormGroup;
    public splitGroupAccountsMap: Record<string, ISplitGroupAccount> = {};
    public splitPreview: { activeAccount: any; passiveAccount: any } | undefined;
    public financialLineItems: any[] = []; // You'll need to populate this

    // Masking (Rename)
    public maskedNamesMap: Record<string, string> = {};

    // Hide Subtotals
    public hiddenSubtotalsSet: Set<string> = new Set();

    // Confirm Dialog
    public showConfirmDialog: boolean = false;
    public confirmText: string = '';
    /** Action-button caption; per-flow so a destructive confirm can name what it does. */
    public confirmActionText: string = '';
    pendingConfirmAction: string;
    pendingUngroupId: string;

    /** REV-21798: accounts awaiting the delete confirmation. */
    private pendingReclassAccountDeletions: IReclassificationAccountRef[] = [];
    /**
     * REV-21798: reclass accounts deleted in this session that may still exist
     * in the persisted `finStatements`. Consumed by `getAnnualReportToBeSaved`
     * to strip them from the payload, then cleared on save success.
     *
     * Orphan pruning cannot cover this: an account someone else reclassifies
     * *into* stays referenced, so it would survive the prune even though the
     * user deleted it.
     */
    private deletedReclassAccounts: IReclassificationAccountRef[] = [];

    // ========================================
    // NEW: Map of drop list IDs grouped by parent FLI
    // Each FLI and its child groups/sub-FLIs can only interact with each other
    // ========================================
    public dropListIdsByFli: Map<string, string[]> = new Map();

    // ========================================
    // REV-20899: Reclassification transactions
    // ========================================
    /** Live store mirror; updated whenever the store emits. */
    public reclassTransactions: IReclassificationTransaction[] = [];
    /** Version flag from the AR's groupingAndMasking. Engine derivation only runs at 1. */
    public reclassificationsVersion: 0 | 1 | undefined = undefined;
    /** Modal state. */
    public reclassModalOpen = false;
    public reclassModalContext: {
        sourceRef: IReclassificationAccountRef;
        sourceLabel: string;
        sourceBalance: number | null;
        period: ReclassificationPeriod;
        statement: ReclassificationStatement;
        sourceSide: 'AKTIV' | 'PASSIV' | null;
        availableTargets: ReclassTargetOption[];
        existingOutgoing: IReclassificationTransaction[];
        existingIncoming: IReclassificationTransaction[];
    } | null = null;
    /** Nested new-account modal state. */
    public newAccountModalOpen = false;
    public newAccountModalStatement: ReclassificationStatement | null = null;
    /**
     * The "+" button in the reclassification modal now asks WHAT to create — an account or a group —
     * before opening either form. `newTargetChoiceOpen` is that first step; the two flags below are
     * its branches.
     */
    public newTargetChoiceOpen = false;
    public newGroupModalOpen = false;
    /**
     * Height (px) of the reclassification dialog measured the moment a nested dialog opens.
     *
     * The nested dialogs adopt it, so the window the user is looking at keeps the same box while the
     * flow steps through chooser → account/group form. Null until measured (then the dialog sizes
     * itself, as before).
     */
    public nestedReclassDialogHeight: number | null = null;

    /**
     * Set of composite `accountNumber|name` keys (2-part, no UUID) that belong to
     * true-duplicate account pairs in the current fin statements.
     * Rebuilt by rebuildReclassAccountIndex().
     */
    private duplicateAccountKeys = new Set<string>();
    /**
     * Metadata for accounts that were assigned a uniqueAccountId — keyed by UUID.
     * Used to enrich the target-account dropdown with distinguishing info (FLI name,
     * closing balance) so users can tell apart duplicate accounts.
     */
    private uniqueAccountIdMeta = new Map<string, { fliName: string; closingBalance: number; openingBalance: number }>();
    /** True when the user has assigned uniqueAccountIds in the current session
     *  but has not yet saved — triggers a version bump in getAnnualReportToBeSaved. */
    private pendingUniqueAccountIds = false;
    private newAccountModalCallback: ((ref: IReclassificationAccountRef) => void) | null = null;
    /** Set alongside the account callback; used by the GROUP branch, which has no account ref yet. */
    private newTargetSelectionCallback: ((selection: ReclassTargetSelection) => void) | null = null;
    /** Conflict view state. */
    public reclassConflictsViewOpen = false;
    public reclassConflictCount = 0;
    /** REV-20899: Preview / Umgliederungsspiegel modal state. */
    public reclassPreviewOpen = false;
    /** Cached statement->accounts index, rebuilt when the table is rendered. */
    private accountsByStatement: Record<ReclassificationStatement, Array<{ accountNumber: string; name: string; uniqueAccountId?: string }>> = {
        [ReclassificationStatement.BALANCE_SHEET]: [],
        [ReclassificationStatement.INCOME_STATEMENT]: [],
    };
    /**
     * REV-20899: session-scoped list of phantom reclasses accounts the user
     * has created in this session but not yet saved into `finStatements`.
     *
     * On render, these are injected into the cloned view model's FLI
     * `reclassesAccounts` arrays so they show up in the table immediately.
     * On save, they are merged into a cloned copy of `finStatements` (see
     * `getAnnualReportToBeSaved`) — at which point they become ordinary
     * `IReclassesAccounts` entries on the AR and flow through every
     * downstream step (notes / AoP / EMS / FAS / print preview) like any
     * other reclasses account. After save success, this list is cleared.
     *
     * Existing reclasses accounts (loaded from the server / created by
     * other features) live directly on `finStatements...fli.reclassesAccounts`
     * and do not pass through this list — they keep working unchanged.
     */
    public pendingNewReclassesAccounts: INewReclassesAccountSessionEntry[] = [];

    /**
     * REV-20899: when true, the previous-year reclass column is locked —
     * clicking a PY reclass cell does NOT open the reclassification modal.
     * Existing PY transactions are still displayed; the lock only gates
     * the editor entry point. Toggled by a lock icon next to the PY
     * reclass column header and persisted on
     * `IGroupingMasking.reclassPyLocked`.
     */
    public reclassPyLocked = true;
    /**
     * True when this is a legacy report that already carries
     * legacy reclasses (`reclassificationsVersion !== 1 && hasLegacyReclasses`).
     * The new transactional reclass feature is then disabled here — the user
     * must keep editing reclasses in the old "Umgliederungen & Stille Reserven"
     * tab. Grouping / masking / sorting stay fully usable. Computed once per AR
     * load in `loadGroupingAndMaskingFromAR`.
     */
    public reclassFeatureDisabled = false;
    /** Map balance / income-statement enum → IReclassificationStatement for convenience. */
    public readonly ReclassificationStatement = ReclassificationStatement;
    public readonly ReclassificationPeriod = ReclassificationPeriod;

    // Localized Texts
    aumico_grouping_masking_define_bsa: any;
    aumico_grouping_masking_remove_bsa: any;
    aumico_grouping_masking_ungroup: any;
    aumico_grouping_masking_remove_from_group: any;
    aumico_grouping_masking_remove_original_name: any;
    aumico_grouping_masking_rename_modal_title: any;
    aumico_grouping_masking_rename_modal_text: any;
    aumico_grouping_masking_rename_modal_label_original: any;
    aumico_grouping_masking_rename_modal_label_new: any;
    aumico_grouping_masking_rename_modal_label_placeholder: any;
    aumico_grouping_masking_rename_modal_label_delete: any;
    aumico_grouping_masking_modal_apply: any;
    aumico_grouping_masking_modal_cancel: any;
    aumico_grouping_masking_grouping_modal_title: any;
    aumico_grouping_masking_grouping_modal_text: any;
    aumico_grouping_masking_grouping_modal_label_placeholder: any;
    aumico_grouping_masking_split_modal_title: any;
    aumico_grouping_masking_split_modal_title_start: any;
    aumico_grouping_masking_split_modal_title_output: any;
    aumico_grouping_masking_split_modal_title_counterpart: any;
    aumico_grouping_masking_split_modal_title_counterpart_placeholder: any;
    aumico_grouping_masking_modal_confirm: any;
    aumico_grouping_masking_modal_dismiss: any;
    aumico_annual_report_import_form_balance_title: any;
    aumico_annual_report_import_form_income_title: any;
    aumico_templates_group_account_mapping_of_that_item_text: any;
    aumico_grouping_masking_confirm_unsplitt_text: (value: string) => any;
    aumico_grouping_masking_confirm_ungroup_text: (value: string) => any;
    /** REV-21798: tooltip on the reclass-account marker icon. */
    reclassAccountBadgeTooltip = '';
    /**
     * REV-21798: name prefix for a generated reclass account, in the report's language
     * ("Umgliederung aus" / "Reclassification from" / …). Read once per load; the name it builds
     * is persisted, while the reuse lookup stays language-agnostic.
     */
    reclassGeneratedAccountNamePrefix = '';
    /**
     * Localized "to" joining the source and the target GROUP in a generated account's name
     * ("Umgliederung aus X nach Gruppe Y"). Read once per load like the prefix above; the name it
     * builds is persisted, while the reuse lookup stays language-agnostic.
     */
    reclassGeneratedAccountNameConnector = '';

    constructor(
        injector: Injector,
        public override aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        protected override actionsListener$: ActionsSubject,
        private readonly mixpanelService: MixpanelService,
        private readonly annualReportProvider: AnnualReportProvider,
        private readonly dateLocalePipe: DateLocalePipe,
        dialog: MatDialog,
        private readonly fb: FormBuilder,
        private readonly cdr: ChangeDetectorRef,
        private readonly i18nextService: I18NextService,
        public readonly reclassStore: ReclassificationStoreService,
    ) {
        super(injector, actionsListener$, aumicoCoreLayoutProvider, dialog);
        this.currentAnnualReportStep = AnnualReportStep.IMPORT_BALANCE_INCOME;
        this.isPartOfArNavigation$ = this.annualReportNavigationProvider.isPartOfArNavigation$(
            AnnualReportStep.IMPORT_BALANCE_INCOME,
        );
        this.renameForm = this.fb.group({});
        this.groupForm = this.fb.group({});
        this.splitForm = this.fb.group({});
    }

    override ngOnInit(): void {
        this.mixpanelService.time_event(MIXPANEL_EVENTS.GROUPING_MASKING);
        super.ngOnInit();
        this.initTexts();
        // Initialize the IndexedDB connection first (for collapse state)
        this.openDB().catch((e) => debug('Failed to open IDB:', e));

        this.annualReportProvider
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                find((activeAnnualReportEncryptedId) => activeAnnualReportEncryptedId != null),
                untilDestroyed(this),
            )
            .subscribe((activeAnnualReportEncryptedId) => {
                this.annualReportProvider.dispatchActiveAnnualReport(activeAnnualReportEncryptedId);
            });

        this.annualReportProvider
            .getActiveDueDates$()
            .pipe(untilDestroyed(this))
            .subscribe((dueDates: DueDates) => {
                this.dueDateCY = dueDates.dueDate;
                this.dueDatePY = dueDates.dueDatePY ? dueDates.dueDatePY : '-';
                this.periodCY =
                    this.dateLocalePipe.transform(dueDates.periodFrom).toString() +
                    ' <br>-' +
                    this.dateLocalePipe.transform(dueDates.periodTo);
                this.periodPY = dueDates.dueDatePY
                    ? this.dateLocalePipe.transform(dueDates.periodFromPY).toString() +
                    ' <br>-' +
                    this.dateLocalePipe.transform(dueDates.periodToPY)
                    : '-';
            });

        // ----------------------------------------------------------------------
        // 🚀 COMBINED SUBSCRIPTION: Wait for AR ID and Fin Statements
        // ----------------------------------------------------------------------
        combineLatest([
            // Cast to include the expected groupingAndMasking field.
            // REV-20899 save-side isolation: the ANNUAL REPORT object is what
            // getAnnualReportToBeSaved() persists, so it MUST come from the RAW
            // selector. The reclass-materialized (bridged) finStatements have their
            // openingReClass/closingReClass zeroed then re-derived from transactions;
            // persisting that view would destroy a v1 report's inherited legacy
            // reclasses (the Case-3 frozen reference the old tab reads). Only the
            // DISPLAY finStatements below stay reclass-aware, so the grid, reclass
            // cells and modal balance subtitle still reflect the transactions.
            this.annualReportProvider.getActiveAnnualReport$() as Observable<IAnnualReport>,
            this.annualReportProvider.getActiveFinStatementsWithReclasses$(),
        ])
            .pipe(
                untilDestroyed(this),
                // Ensure we have the Annual Report ID and valid financial statements
                filter(([ar, finStatements]) => !!ar && !!ar._id && finStatements && finStatements.length >= 0),
                tap(([ar, finStatements]) => {
                    // 1. Store the Annual Report & ID
                    this.annualReport = ar;
                    this.annualReportId = ar._id;
                    this.currency = ar.generalInformation?.currency?.name;
                    this.finStatements = finStatements;
                    // 2. Load Sorting State (Synchronous read from fetched AR object)
                    this.loadGroupingAndMaskingFromAR(ar.groupingAndMasking);

                    // 3. Load Collapse State (Asynchronous read from IndexedDB)
                    this.loadCollapseStateFromIDB()
                        .then(() => {
                            debug('Collapse state loaded from IDB, initializing financial statements.');
                        })
                        .catch((error) => {
                            debug('Error loading collapse state from IDB:', error);
                            // Set defaults if IDB fails
                            this.collapseState = new Set();
                        })
                        .finally(() => {
                            // 4. Initialize data once both AR data and local state are ready
                            this.renderTable();
                        });
                    this.disableProceedButton = false;
                    this.footerExtraButtonActions[0].disabled = false;
                }),
            )
            .subscribe();

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportSuccessfully), debounceTime(500), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportFailed), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());

        // REV-20899: keep the local reclass mirror in sync with the store.
        this.reclassStore.transactions$.pipe(untilDestroyed(this)).subscribe((txns) => {
            this.reclassTransactions = txns;
            this.reclassConflictCount = this.computeConflictCount();
            // Keep groupingAndMaskingToBeSaved in sync so that
            // detectChangesInGroupingAndMasking() sees the latest reclassifications.
            this.persistGroupingAndMasking();
            // FIX #1: Re-render the table to apply reclass values to view models
            // and ensure instant reflection of reclasses in table display
            this.renderTable();
        });
    }

    /**
     * Counts unresolvable conflict cards (unique missing account refs)
     * — same shape the conflicts view renders, so the banner number
     * matches the visible card count. A single transaction with both
     * source and target unresolved counts as 2 cards (one per ref).
     */
    private computeConflictCount(): number {
        const conflicts = findConflicts(this.reclassTransactions, this.accountsByStatement);
        return groupConflictsByAccount(conflicts, this.accountsByStatement).length;
    }

    /** Rebuild the per-statement account index from the loaded financial statements. Call after engine recompute. */
    public rebuildReclassAccountIndex(): void {
        // Clear here (once per rebuild) so the walkFli pass below can repopulate
        // uniqueAccountIdMeta with fresh FLI-name / balance data for every UUID-
        // bearing account visible in the view model.  rebuildDuplicateAccountKeys()
        // (called at the end of this method) must NOT clear it again.
        this.uniqueAccountIdMeta.clear();

        const collect = (
            group: IAccountMappingTemplateGroup | undefined,
        ): Array<{ accountNumber: string; name: string; uniqueAccountId?: string }> => {
            const out: Array<{ accountNumber: string; name: string; uniqueAccountId?: string }> = [];
            if (group == null) return out;
            const walkFli = (fli: any): void => {
                // Capture FLI name here so every account push below can record it
                // in uniqueAccountIdMeta for enriched dropdown labels.
                const fliName: string = fli?.name ?? fli?.label ?? '';
                if (Array.isArray(fli?.accounts)) {
                    for (const a of fli.accounts) {
                        if (a == null) continue;
                        if (a.type === ItemType.SUB_FLI || a.type === ItemType.OF_THAT_ITEM) {
                            if (Array.isArray(a.accounts)) {
                                for (const sa of a.accounts) {
                                    if (sa) {
                                        out.push({ accountNumber: sa.accountNumber, name: sa.name ?? '', uniqueAccountId: sa.uniqueAccountId });
                                        if (sa.uniqueAccountId) {
                                            this.uniqueAccountIdMeta.set(sa.uniqueAccountId, {
                                                fliName,
                                                closingBalance: sa.closingBalance ?? 0,
                                                openingBalance: sa.openingBalance ?? 0,
                                            });
                                        }
                                    }
                                }
                            }
                            if (Array.isArray(a.reclassesAccounts)) {
                                for (const ra of a.reclassesAccounts) {
                                    if (ra) out.push({ accountNumber: ra.accountNumber, name: ra.name ?? '', uniqueAccountId: (ra as any).uniqueAccountId });
                                }
                            }
                        } else if (a.accountNumber != null) {
                            // If this is a grouped-summary account (applyGroupingAndSummarize
                            // replaced the individual accounts with a summary node whose
                            // accountNumber is the groupId), also push the individual children
                            // so that conflict detection and target-dropdown lookups can still
                            // resolve them by their original accountNumber + name.
                            if (a.groupId && Array.isArray(a.accounts) && a.accounts.length > 0) {
                                for (const child of a.accounts) {
                                    if (child == null || child.accountNumber == null) continue;
                                    const childUid: string | undefined = (child as any).uniqueAccountId;
                                    out.push({ accountNumber: child.accountNumber, name: child.name ?? '', uniqueAccountId: childUid });
                                    if (childUid) {
                                        this.uniqueAccountIdMeta.set(childUid, {
                                            fliName,
                                            closingBalance: child.closingBalance ?? 0,
                                            openingBalance: child.openingBalance ?? 0,
                                        });
                                    }
                                }
                            } else {
                                out.push({ accountNumber: a.accountNumber, name: a.name ?? '', uniqueAccountId: a.uniqueAccountId });
                                if (a.uniqueAccountId) {
                                    this.uniqueAccountIdMeta.set(a.uniqueAccountId, {
                                        fliName,
                                        closingBalance: a.closingBalance ?? 0,
                                        openingBalance: a.openingBalance ?? 0,
                                    });
                                }
                            }
                        }
                    }
                }
                if (Array.isArray(fli?.reclassesAccounts)) {
                    for (const ra of fli.reclassesAccounts) {
                        if (ra) out.push({ accountNumber: ra.accountNumber, name: ra.name ?? '', uniqueAccountId: (ra as any).uniqueAccountId });
                    }
                }
            };
            // Sub-groups can be nested inside `sortedItems` (post-sort) and
            // also live on the canonical `groups` array. Recurse into both so
            // FLIs under sub-sections are not missed.
            const isGroup = (item: any): boolean => item != null && 'sortedItems' in item;
            const walkGroup = (g: any): void => {
                // Grouping-masking view models populate `sortedItems` (the
                // rendered list). Fall back to `items` for completeness.
                const itemList = (Array.isArray(g?.sortedItems) ? g.sortedItems : null) ?? (Array.isArray(g?.items) ? g.items : null);
                if (itemList) {
                    for (const item of itemList) {
                        if (item == null) continue;
                        if (item.type === ItemType.FLI) walkFli(item);
                        else if (isGroup(item)) walkGroup(item);
                    }
                }
                if (Array.isArray(g?.groups)) {
                    for (const sub of g.groups) walkGroup(sub);
                }
            };
            walkGroup(group);

            // Deduplicate by 3-part key (accountNumber|name|uniqueAccountId):
            //  - Two instances with same accountNumber+name but DIFFERENT uniqueAccountId
            //    values are kept as separate entries (disambiguated duplicates).
            //  - Two instances with same accountNumber+name and NO uniqueAccountId
            //    are collapsed to one (prevents false "ambiguous" conflicts for the
            //    same account appearing in both fli.accounts and fli.reclassesAccounts).
            const seen = new Set<string>();
            return out.filter((a) => {
                const k = a.uniqueAccountId
                    ? `${a.accountNumber}|${a.name}|${a.uniqueAccountId}`
                    : `${a.accountNumber}|${a.name}`;
                if (seen.has(k)) return false;
                seen.add(k);
                return true;
            });
        };

        const bsAccounts = collect(this.balanceSheet);
        const isAccounts = collect(this.incomeStatement);
        this.accountsByStatement = {
            [ReclassificationStatement.BALANCE_SHEET]: bsAccounts,
            [ReclassificationStatement.INCOME_STATEMENT]: isAccounts,
        };

        // Rebuild the set of 2-part keys that have true unresolved duplicates
        // (same accountNumber+name, no uniqueAccountId assigned yet).
        // Use the raw finStatements to detect true duplicates (the view model
        // deduplicates entries, so checking it alone would miss real duplicates).
        this.rebuildDuplicateAccountKeys();
        this.reclassConflictCount = this.computeConflictCount();
    }

    /**
     * Populate `this.duplicateAccountKeys` with 2-part composite keys
     * (accountNumber|name) for accounts that come from more than one import row
     * in the flat `templateAndAccountsMapping.accounts` list for a given statement.
     *
     * A true duplicate account = same accountNumber AND name on two or more
     * import rows. Entries that share an import row, such as the legs of a
     * balance-switch account, count as one row.
     */
    private rebuildDuplicateAccountKeys(): void {
        this.duplicateAccountKeys = new Set<string>();
        // NOTE: uniqueAccountIdMeta is NOT cleared here.  It is cleared once at
        // the start of rebuildReclassAccountIndex() (which always calls this method)
        // and repopulated during the collect() → walkFli() pass above so that FLI
        // name / balance labels are always fresh when the modal opens.
        if (!Array.isArray(this.finStatements)) return;
        for (const fs of this.finStatements) {
            const accounts = fs?.templateAndAccountsMapping?.accounts;
            if (!Array.isArray(accounts)) continue;
            // Import rows per account; an entry without a rowNo counts as a row of its own.
            const rows = new Map<string, Set<unknown>>();
            for (let index = 0; index < accounts.length; index++) {
                const acc = accounts[index];
                if (acc == null || acc.accountNumber == null) continue;
                if (acc.uniqueAccountId) continue; // already disambiguated — skip
                const k = `${acc.accountNumber}|${acc.name ?? ''}`;
                const accountRows = rows.get(k) ?? new Set<unknown>();
                accountRows.add(acc.rowNo ?? `entry-${index}`);
                rows.set(k, accountRows);
            }
            for (const [k, accountRows] of rows) {
                if (accountRows.size >= 2) this.duplicateAccountKeys.add(k);
            }
        }
    }

    /** Net reclass sum for a single account in a given period — used by the table cells. */
    public reclassSumForAccount(
        account: { accountNumber: string; name?: string | null; uniqueAccountId?: string },
        period: ReclassificationPeriod,
    ): number {
        if (account == null || account.accountNumber == null) return 0;
        const ref = { accountNumber: account.accountNumber, name: account.name ?? '', uniqueAccountId: account.uniqueAccountId };
        let sum = getReclassSumForRef(this.reclassTransactions, ref, period);

        // PY stale-UUID fallback
        // ----------------------
        // refsEqual's strict one-UUID rule returns false whenever exactly one
        // side carries a uniqueAccountId, making the transaction invisible in
        // the cell. This arises in two symmetric scenarios for autofilled PY
        // transactions on non-duplicate accounts:
        //
        //   A) Account has NO UUID, transaction ref HAS one — the previous year
        //      saved the account with a UUID (due to a bug in
        //      collectTemplateAccountsByTwoPartKey that falsely treated
        //      OF_THAT_ITEM sub-accounts as inter-FLI duplicates) and that UUID
        //      was carried into the shifted PY transaction.
        //
        //   B) Account HAS a UUID (incorrectly assigned during autofill — same
        //      bug), transaction ref has NO UUID — the CY transaction was
        //      created before the UUID was assigned, so it has no UUID, but the
        //      account node got a UUID during the autofill save step.
        //
        // Safety gate: only apply when the account is an unambiguous singleton
        // (composite key NOT in duplicateAccountKeys). This prevents the
        // fallback from firing for genuine duplicate pairs where the strict UUID
        // rule is required to avoid cross-row value leakage.
        if (period === ReclassificationPeriod.PREVIOUS_YEAR) {
            const compositeKey = `${account.accountNumber}|${account.name ?? ''}`;
            if (!this.duplicateAccountKeys.has(compositeKey)) {
                const accNum = account.accountNumber;
                const accName = account.name ?? '';
                const accUid = account.uniqueAccountId;
                for (const t of this.reclassTransactions ?? []) {
                    if (t.period !== period) continue;
                    // Only handle UUID-mismatch cases the main pass missed:
                    // exactly one of (transaction ref, account) carries a UUID.
                    const srcUid = t.source.uniqueAccountId;
                    const tgtUid = t.target.uniqueAccountId;
                    if ((!!srcUid !== !!accUid) &&
                        t.source.accountNumber === accNum &&
                        (t.source.name ?? '') === accName) {
                        sum -= t.amount;
                    }
                    if ((!!tgtUid !== !!accUid) &&
                        t.target.accountNumber === accNum &&
                        (t.target.name ?? '') === accName) {
                        sum += t.amount;
                    }
                }
            }
        }

        return sum;
    }

    /**
     * REV-21798: account number as shown in the table.
     *
     * System-created reclassification accounts render as `MAN`; their real
     * generated number stays the identity everywhere else (transaction refs,
     * grouping identifiers, sorting state, save payload).
     */
    public displayAccountNumberFor(acc: { type?: string; accountNumber?: string } | null | undefined): string {
        if (acc == null) return '';
        if (acc.type === 'reclasses') return '-';
        return displayAccountNumber(acc.accountNumber);
    }

    /** REV-21798: true for rows the user created via a reclassification. */
    public isReclassAccountRow(acc: { isReclassAccount?: boolean } | null | undefined): boolean {
        return acc?.isReclassAccount === true;
    }

    /** Returns a stable composite ref for an account, used by the cell + modal. */
    public reclassRefForAccount(account: {
        accountNumber: string;
        name?: string | null;
        uniqueAccountId?: string;
    }): IReclassificationAccountRef {
        return {
            accountNumber: account.accountNumber,
            name: account.name ?? '',
            uniqueAccountId: account.uniqueAccountId,
        };
    }

    /**
     * REV-21798: build the reclassification target dropdown.
     *
     * ONE flat list in the SAME ORDER the Grouping & Masking table renders — FLI, then its
     * accounts, then a SubFLI and its accounts, then a group and its members, and so on. The list
     * deliberately does NOT separate containers from accounts: the user picks a target by finding
     * it where they already see it on screen, so mirroring the table beats any grouping we could
     * impose on it.
     *
     * Labels are what the table shows and nothing more:
     *   account   `<number> <name>`  (number is `MAN` for system-created reclass accounts)
     *   container the caption alone — no parent prefix
     *
     * Mapping template groups are intentionally absent: accounts cannot be mapped directly into
     * them, only into the FLIs/SubFLIs they contain.
     *
     * Container options carry their resolved host (`hostFliId` / `hostSubFliId`) so
     * `materializeReclassTarget` can place the generated `MAN` account without walking the tree
     * a second time.
     */
    private buildReclassTargetOptions(
        sourceRef: IReclassificationAccountRef,
        statement: ReclassificationStatement,
    ): ReclassTargetOption[] {
        const root = statement === ReclassificationStatement.BALANCE_SHEET ? this.balanceSheet : this.incomeStatement;
        const out: ReclassTargetOption[] = [];
        if (root == null) return out;

        // A group is reachable through BOTH `sortedItems` and `groups[]`, so the same node is
        // visited more than once. Dedupe by option value; first occurrence wins, which preserves
        // the render order.
        const seen = new Set<string>();
        const push = (selection: ReclassTargetSelection, label: string): void => {
            const value = targetSelectionKey(selection);
            if (seen.has(value)) return;
            seen.add(value);
            out.push({ value, label, statement, selection });
        };

        const isSourceAccount = (acc: any): boolean => {
            if (sourceRef.uniqueAccountId) return acc?.uniqueAccountId === sourceRef.uniqueAccountId;
            return acc?.accountNumber === sourceRef.accountNumber && (acc?.name ?? '') === sourceRef.name;
        };

        const pushAccount = (acc: any): void => {
            // A numberless row (the transferred annual result) is not a posting account and cannot be
            // addressed by a transaction ref — see `canReclassAccount`.
            if (!this.canReclassAccount(acc)) return;
            if (isSourceAccount(acc)) return; // an account cannot reclassify onto itself
            const uid: string | undefined = acc.uniqueAccountId;
            const twoPartKey = `${acc.accountNumber}|${acc.name ?? ''}`;
            // Enrich ONLY true duplicates, so the user can tell the two instances apart.
            const meta = uid && this.duplicateAccountKeys.has(twoPartKey) ? this.uniqueAccountIdMeta.get(uid) : undefined;
            // The DISPLAY name, so a manual rename of a generated account — the sanctioned way to
            // correct a recorded group after moving or renaming it — shows up here too.
            const base = `${displayAccountNumber(acc.accountNumber)} ${this.getAccountDisplayName(
                acc.accountNumber,
                acc.name ?? '',
                acc.shareholder,
                acc.uniqueAccountId,
            )}`.trim();
            push(
                { kind: 'ACCOUNT', ref: { accountNumber: acc.accountNumber, name: acc.name ?? '', uniqueAccountId: uid } },
                meta ? `${base} · ${meta.fliName} (${meta.closingBalance.toFixed(2)})` : base,
            );
        };

        /** Walk an FLI's or SubFLI's children in render order. */
        const walkContainer = (node: any, hostFliId: string, hostSubFliId?: string): void => {
            for (const child of Array.isArray(node?.accounts) ? node.accounts : []) {
                if (child == null) continue;

                if (child.type === ItemType.SUB_FLI || child.type === ItemType.OF_THAT_ITEM) {
                    const caption = child.caption ?? child.name;
                    push(
                        {
                            kind: 'SUBFLI',
                            containerId: child.sortingId ?? child.name,
                            label: caption,
                            hostFliId,
                            hostSubFliId: child.name,
                        },
                        caption,
                    );
                    walkContainer(child, hostFliId, child.name);
                    continue;
                }

                // A grouped-summary node produced by applyGroupingAndSummarize: the group row
                // itself is a target, and its members render underneath it.
                if (child.groupId && Array.isArray(child.accounts)) {
                    const caption = child.groupName ?? child.name;
                    push(
                        {
                            kind: 'GM_GROUP',
                            containerId: child.groupId,
                            label: caption,
                            hostFliId,
                            hostSubFliId,
                            groupId: child.groupId,
                        },
                        caption,
                    );
                    for (const member of child.accounts) pushAccount(member);
                    continue;
                }

                pushAccount(child);
            }
        };

        const isGroup = (item: any): boolean => item != null && 'sortedItems' in item;
        const walkGroup = (g: any): void => {
            const itemList =
                (Array.isArray(g?.sortedItems) ? g.sortedItems : null) ?? (Array.isArray(g?.items) ? g.items : null);
            if (itemList) {
                for (const item of itemList) {
                    if (item == null) continue;
                    if (item.type === ItemType.FLI) {
                        const fliId: string = item.name;
                        if (!fliId) continue;
                        const caption = item.caption ?? fliId;
                        push({ kind: 'FLI', containerId: item.sortingId ?? fliId, label: caption, hostFliId: fliId }, caption);
                        walkContainer(item, fliId);
                    } else if (isGroup(item)) {
                        walkGroup(item);
                    }
                }
            }
            if (Array.isArray(g?.groups)) {
                for (const sub of g.groups) walkGroup(sub);
            }
        };

        walkGroup(root);
        return out;
    }

    /**
     * REV-20899: "final" CY value displayed in the grouping table.
     * The engine aggregates `calculatedClosingBalance` and `closingReClass` as
     * separate fields; the displayed value must combine them so reclassifications
     * are visible in every row (account, group, FLI, section) and in totals.
     *
     * For an account this is the imported close balance plus net reclass.
     * For an aggregate this is the sum of children's `calculatedClosingBalance`
     * plus the sum of children's `closingReClass` — already aggregated by the
     * engine into the aggregate's own `closingReClass`.
     */
    public finalCY(item: { calculatedClosingBalance?: number | null; closingReClass?: number | null } | null | undefined): number {
        return GroupingMaskingComponent.finalCYOf(item);
    }

    public static finalCYOf(item: { calculatedClosingBalance?: number | null; closingReClass?: number | null } | null | undefined): number {
        return GroupingMaskingHelper.finalCYOf(item);
    }

    /** REV-20899: "final" PY value (imported opening balance + net opening reclass). */
    public finalPY(item: { calculatedOpeningBalance?: number | null; openingReClass?: number | null } | null | undefined): number {
        return GroupingMaskingComponent.finalPYOf(item);
    }

    public static finalPYOf(item: { calculatedOpeningBalance?: number | null; openingReClass?: number | null } | null | undefined): number {
        return GroupingMaskingHelper.finalPYOf(item);
    }

    /**
     * REV-20899: merged list of row entries for an FLI's body — the imported
     * `accounts` first, followed by any phantom reclass accounts stored on
     * `reclassesAccounts`. The grouping/masking table renders this combined
     * list so user-created phantoms appear under their selected FLI alongside
     * regular accounts.
     *
     * Defensively returns a fresh array on every call only if a merge is
     * actually needed — when there are no phantoms, we hand back the
     * original `accounts` reference so Angular's `*ngFor` change-detection
     * remains as cheap as before for the common case.
     */
    public fliRowEntries(item: { accounts?: any[]; reclassesAccounts?: any[] } | null | undefined): any[] {
        if (item == null) return [];
        const accounts = Array.isArray(item.accounts) ? item.accounts : [];
        const reclassesAccounts = Array.isArray(item.reclassesAccounts) ? item.reclassesAccounts : [];
        if (reclassesAccounts.length === 0) return accounts;
        return [...accounts, ...reclassesAccounts];
    }

    /**
     * Determine the source side (AKTIV/PASSIV) of an account on the balance sheet, used by the cross-side warning.
     * Returns null for IS accounts.
     */
    public sideOfAccount(ref: IReclassificationAccountRef, statement: ReclassificationStatement): 'AKTIV' | 'PASSIV' | null {
        if (statement === ReclassificationStatement.INCOME_STATEMENT) return null;
        if (!this.balanceSheet || !Array.isArray(this.balanceSheet.groups)) return null;
        // The balance sheet root has two top groups: Assets (AKTIV) and Liabilities (PASSIV) typically as groups[0] and groups[1].
        /** Match helper: prefers uniqueAccountId when present. */
        const matchRef = (a: any): boolean => {
            if (ref.uniqueAccountId) return a?.uniqueAccountId === ref.uniqueAccountId;
            return a?.accountNumber === ref.accountNumber && (a?.name ?? '') === ref.name;
        };
        const findInGroup = (g: any): boolean => {
            const itemList = (Array.isArray(g?.sortedItems) ? g.sortedItems : null) ?? (Array.isArray(g?.items) ? g.items : null);
            if (itemList) {
                for (const item of itemList) {
                    if (item == null) continue;
                    if (item.type === ItemType.FLI && Array.isArray(item.accounts)) {
                        for (const a of item.accounts) {
                            if (a == null) continue;
                            if (matchRef(a)) return true;
                            if (a.type === ItemType.SUB_FLI || a.type === ItemType.OF_THAT_ITEM) {
                                if (Array.isArray(a.accounts)) {
                                    for (const sa of a.accounts) {
                                        if (matchRef(sa)) {
                                            return true;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            if (Array.isArray(g?.groups)) {
                for (const sub of g.groups) {
                    if (findInGroup(sub)) return true;
                }
            }
            return false;
        };
        const assets = this.balanceSheet.groups[0];
        const liabilities = this.balanceSheet.groups[1];
        if (assets && findInGroup(assets)) return 'AKTIV';
        if (liabilities && findInGroup(liabilities)) return 'PASSIV';
        return null;
    }

    /** Open the reclassification modal for an account+period. */
    public onReclassCellClick(args: {
        accountRef: IReclassificationAccountRef;
        period: ReclassificationPeriod;
        statement: ReclassificationStatement;
        /** rowNo of the clicked account — used to identify the specific duplicate instance. */
        rowNo?: number;
    }): void {
        // Legacy report with existing reclasses: the new feature is disabled
        // (reclassFeatureDisabled), so cell clicks are inert (belt-and-suspenders with the
        // cell-level [disabled] binding, and covers programmatic callers).
        if (this.reclassFeatureDisabled) {
            return;
        }
        // REV-20899: PY column is read-only when the lock is engaged.
        if (this.reclassPyLocked && args.period === ReclassificationPeriod.PREVIOUS_YEAR) {
            return;
        }
        // Belt-and-braces with the template: a numberless row renders no cell to click.
        if (!this.canReclassAccount(args.accountRef)) {
            return;
        }

        // REV-DUPLICATE: if this account is a true duplicate (same accountNumber+name)
        // and has not yet been assigned a uniqueAccountId, auto-assign one now by
        // walking the view model. The user has already identified the specific instance
        // by clicking on a specific row — we use rowNo to match it.
        const twoPartKey = `${args.accountRef.accountNumber}|${args.accountRef.name}`;
        if (this.duplicateAccountKeys.has(twoPartKey) && !args.accountRef.uniqueAccountId) {
            const assignedIds = this.assignUniqueAccountIds(
                args.accountRef.accountNumber,
                args.accountRef.name,
                args.statement,
            );
            // Identify which UUID belongs to the clicked row.
            const chosen = assignedIds.find((e) =>
                args.rowNo != null ? e.rowNo === args.rowNo : e.positionIndex === 0,
            );
            if (chosen) {
                // Re-render the table so accountsByStatement picks up the new UUIDs.
                this.renderTable();
                this.openReclassModal({
                    accountRef: { ...args.accountRef, uniqueAccountId: chosen.uniqueAccountId },
                    period: args.period,
                    statement: args.statement,
                });
                return;
            }
        }

        this.openReclassModal(args);
    }

    /**
     * Row identity for TRUE DUPLICATES — two accounts sharing BOTH number and name.
     *
     * Everything per-row in this step (the selection checkbox, group membership, renames, the drag
     * order, `trackByAccount`) is keyed on the account identifier, which for such a pair is the
     * SAME string. Ticking one row therefore ticked both, and every action then hit both — the two
     * could not, for instance, go into different groups.
     *
     * The cure is to give each instance its own `uniqueAccountId` (the ids are deterministic —
     * derived from the import row, or from the position when a row carries no `rowNo` — so they come
     * back identical on every render and after a reload) and to let `MaskingHelper.getRowIdentifier`
     * append it. Accounts that are not duplicates get nothing and keep their 2-part identifier, so
     * for them neither the UI nor the saved payload changes.
     *
     * Runs on every render because the view model is rebuilt each time; it is idempotent, and the
     * ids it hands out are stable, so repeated runs converge.
     */
    private ensureDuplicateRowIdentities(): void {
        const rowScopedBases = this.collectRowScopedBaseIdentifiers();
        const pinnedByReclasses = this.baseIdentifiersPinnedByReclassRefs();
        for (const statement of [
            ReclassificationStatement.BALANCE_SHEET,
            ReclassificationStatement.INCOME_STATEMENT,
        ]) {
            const root =
                statement === ReclassificationStatement.BALANCE_SHEET ? this.balanceSheet : this.incomeStatement;
            if (root == null) continue;

            // Count WITHIN a container, and only once per container.
            //
            // The statement tree carries CLONED subtrees — a section is reachable through a parent's
            // `sortedItems` and through its `groups`, and those copies are not the same objects — so
            // counting every leaf the walk yields sees each account several times and would declare a
            // perfectly unique account duplicated. Keying the tally by the container's `sortingId`
            // (stable, and identical across clones) and taking the first copy of each container fixes
            // both halves: clones collapse, and genuine same-container duplicates still count twice.
            //
            // Two rows in DIFFERENT containers (e.g. the two legs of a balance-switch account, one in
            // an asset FLI and one in a liability FLI) are deliberately NOT treated as duplicates
            // here: group membership, selection and sorting are all container-scoped already, so those
            // rows are separable without an id.
            const perBase = new Map<string, { accountNumber: string; name: string; count: number; missing: boolean }>();
            const countedContainers = new Set<string>();
            // Rows per identifier and container; `count` is the highest number found in one container.
            const perContainer = new Map<string, number>();
            for (const row of this.collectLeafRows(root)) {
                const acc = row.account;
                if (acc?.accountNumber == null) continue;
                if (row.containerSortingId === '') continue;
                const containerKey = `${row.containerSortingId}#${row.indexInContainer}`;
                if (countedContainers.has(containerKey)) continue;
                countedContainers.add(containerKey);
                const base = MaskingHelper.getAccountIdentifier(acc.accountNumber, acc.name ?? '', acc.shareholder);
                const entry =
                    perBase.get(base) ??
                    { accountNumber: acc.accountNumber, name: acc.name ?? '', count: 0, missing: false };
                const tallyKey = `${base}@${row.containerSortingId}`;
                const inContainer = (perContainer.get(tallyKey) ?? 0) + 1;
                perContainer.set(tallyKey, inContainer);
                entry.count = Math.max(entry.count, inContainer);
                if (!acc.uniqueAccountId) entry.missing = true;
                perBase.set(base, entry);
            }

            for (const [base, entry] of perBase) {
                if (!entry.missing) continue; // already disambiguated
                // Assign when the pair is actually duplicated, OR when saved state already addresses
                // this account per row: without the second case a report whose duplicate pair became
                // unique in the meantime would stop matching its own saved membership.
                if (entry.count < 2 && !rowScopedBases.has(base)) continue;
                // …but never behind a reclassification's back: `refsEqual` treats a ref WITHOUT a
                // uniqueAccountId and an account WITH one as different accounts, so stamping an id
                // on an account an existing transaction addresses by number+name would silently
                // zero that reclassification. Those pairs keep the shared identifier until the user
                // picks an instance in the reclass flow, which assigns the id AND aims the
                // transaction at the row they clicked (`onReclassCellClick`).
                //
                // Matched WITHOUT the shareholder, because a transaction ref carries only
                // (accountNumber, name) — `refFromAccount` never stores a shareholder. Comparing
                // `base` directly would miss every shareholder account, whose identifier reads
                // `1000#Darlehen - Müller`, and those are exactly the reclassifications that would
                // then be zeroed on open.
                if (pinnedByReclasses.has(MaskingHelper.getAccountIdentifier(entry.accountNumber, entry.name, undefined))) {
                    continue;
                }
                // `base` is the identifier the count was keyed on, shareholder included, so only the
                // rows that actually share an identifier get an id.
                this.assignUniqueAccountIds(entry.accountNumber, entry.name, statement, base);
            }
        }

        this.migrateLegacyDuplicateGroupMembership();
    }

    /**
     * `uniqueAccountId`s already carried by the rows of one (accountNumber, name) in a statement.
     *
     * Read from the view model AND from the raw flat account list, because a persisted id may not be
     * present in both trees at the same moment (the view model is re-cloned on every render, and the
     * mirror step writes the id into finStatements).
     */
    private collectUniqueAccountIds(
        statement: ReclassificationStatement,
        accountNumber: string,
        name: string,
        baseIdentifier?: string,
    ): string[] {
        const out: string[] = [];
        const matches = (acc: any): boolean => {
            if (acc?.accountNumber !== accountNumber || (acc?.name ?? '') !== name || acc?.uniqueAccountId == null) {
                return false;
            }
            if (baseIdentifier == null) return true;
            return (
                MaskingHelper.getAccountIdentifier(acc.accountNumber, acc.name ?? '', acc.shareholder) ===
                baseIdentifier
            );
        };

        const vmRoot =
            statement === ReclassificationStatement.BALANCE_SHEET ? this.balanceSheet : this.incomeStatement;
        for (const row of this.collectLeafRows(vmRoot)) {
            if (matches(row.account)) out.push(row.account.uniqueAccountId);
        }

        const statementIndex = statement === ReclassificationStatement.BALANCE_SHEET ? 0 : 1;
        const flatAccounts = this.finStatements?.[statementIndex]?.templateAndAccountsMapping?.accounts as
            | Array<{ accountNumber?: string; name?: string; uniqueAccountId?: string }>
            | undefined;
        for (const acc of Array.isArray(flatAccounts) ? flatAccounts : []) {
            if (matches(acc)) out.push(acc.uniqueAccountId as string);
        }
        return out;
    }

    /**
     * Every leaf account of a statement — SubFLI children and grouped-summary members included —
     * together with the container it sits in and its position there.
     *
     * The same account comes back SEVERAL times: a section is reachable through a parent's
     * `sortedItems` and through its `groups`, and those copies are CLONES rather than the same
     * objects, so object identity cannot collapse them (measured: four copies of every row). Callers
     * that COUNT must therefore de-duplicate on `containerSortingId` + `indexInContainer`, which is
     * stable across clones; callers that MUTATE want every copy, since the rendered tree is one of
     * them.
     */
    private collectLeafRows(
        root: IAccountMappingTemplateGroup | undefined,
    ): Array<{ account: any; containerSortingId: string; indexInContainer: number }> {
        const out: Array<{ account: any; containerSortingId: string; indexInContainer: number }> = [];
        const pushFrom = (container: any, containerSortingId: string): void => {
            if (container == null) return;
            const accounts = Array.isArray(container.accounts) ? container.accounts : [];
            for (let index = 0; index < accounts.length; index++) {
                const acc = accounts[index];
                if (acc == null) continue;
                if (acc.type === ItemType.SUB_FLI || acc.type === ItemType.OF_THAT_ITEM) {
                    pushFrom(acc, acc.sortingId ?? containerSortingId);
                    continue;
                }
                // A grouped-summary node keeps its members in `accounts`; they are the real rows, and
                // they keep the CONTAINER's sorting id (grouping does not move them).
                if (acc.groupId && Array.isArray(acc.accounts) && acc.accounts.length > 0) {
                    pushFrom(acc, containerSortingId);
                    continue;
                }
                out.push({
                    account: acc,
                    containerSortingId: acc.sortingId ?? containerSortingId,
                    indexInContainer: index,
                });
            }
        };
        const walkGroup = (g: any): void => {
            if (g == null) return;
            const itemList =
                (Array.isArray(g.sortedItems) ? g.sortedItems : null) ?? (Array.isArray(g.items) ? g.items : null);
            if (itemList) {
                for (const item of itemList) {
                    if (item == null) continue;
                    if (item.type === ItemType.FLI) pushFrom(item, item.sortingId ?? '');
                    else if ('sortedItems' in item || 'items' in item) walkGroup(item);
                }
            }
            if (Array.isArray(g.groups)) for (const sub of g.groups) walkGroup(sub);
        };
        walkGroup(root);
        return out;
    }

    /**
     * Identifiers an existing reclassification addresses WITHOUT a `uniqueAccountId`, in the
     * SHAREHOLDER-LESS form the refs themselves carry (`accountNumber#name`).
     *
     * Assigning ids to those accounts would break the transaction (see the call site), so they are
     * left alone here. The call site strips the shareholder before asking, since one such ref stands
     * for every shareholder variant of that number + name.
     */
    private baseIdentifiersPinnedByReclassRefs(): Set<string> {
        const out = new Set<string>();
        for (const transaction of this.reclassTransactions ?? []) {
            for (const ref of [transaction?.source, transaction?.target]) {
                if (ref == null || ref.uniqueAccountId) continue;
                out.add(MaskingHelper.getAccountIdentifier(ref.accountNumber, ref.name ?? '', undefined));
            }
        }
        return out;
    }

    /**
     * Base identifiers that SAVED state already addresses per row — i.e. that carry a row-scoped
     * entry in the grouping, sorting or rename maps.
     */
    private collectRowScopedBaseIdentifiers(): Set<string> {
        const out = new Set<string>();
        const add = (identifier: string): void => {
            if (MaskingHelper.isRowScopedIdentifier(identifier)) {
                out.add(MaskingHelper.baseIdentifierOf(identifier));
            }
        };
        for (const groupId in this.groupedAccountsMap) {
            for (const identifier of this.groupedAccountsMap[groupId]?.accounts ?? []) add(identifier);
        }
        for (const containerId in this.sortingState) {
            for (const identifier of this.sortingState[containerId] ?? []) add(identifier);
        }
        for (const key of Object.keys(this.maskedNamesMap ?? {})) add(key);
        return out;
    }

    /**
     * Rewrite a LEGACY group membership entry for a duplicate pair into one entry per instance.
     *
     * Before row identity existed the app could only write `9001#Alpha Konto`, and that single entry
     * stood for BOTH instances — which is why reads still honour it (see
     * `MaskingHelper.getIdentifierCandidates`). But a blanket entry cannot express "this instance is
     * in the group and that one is not", so the first per-row action on such a pair would either do
     * nothing or move both. Expanding it here — to exactly the instances the blanket entry already
     * covered, so the rendered grouping is unchanged — makes the following action precise.
     *
     * The definitions are mutated in place, which is also what keeps the change out of the
     * unsaved-changes check: the loaded snapshot holds the same definition objects, and the
     * expansion is re-derived on every render anyway, so it does not need to reach the backend to
     * hold. It rides along the next save the user makes for their own reasons.
     */
    private migrateLegacyDuplicateGroupMembership(): void {
        const rowsByBase = new Map<string, Array<{ rowIdentifier: string; containerSortingId: string }>>();
        const seenRows = new Set<string>();
        for (const root of [this.balanceSheet, this.incomeStatement]) {
            for (const row of this.collectLeafRows(root)) {
                const acc = row.account;
                if (acc?.uniqueAccountId == null || acc.accountNumber == null) continue;
                // Clones of one row must contribute ONCE (see collectLeafRows).
                const rowKey = `${row.containerSortingId}#${row.indexInContainer}#${acc.uniqueAccountId}`;
                if (seenRows.has(rowKey)) continue;
                seenRows.add(rowKey);
                const base = MaskingHelper.getAccountIdentifier(acc.accountNumber, acc.name ?? '', acc.shareholder);
                const list = rowsByBase.get(base) ?? [];
                list.push({
                    rowIdentifier: MaskingHelper.getRowIdentifier(acc),
                    containerSortingId: row.containerSortingId,
                });
                rowsByBase.set(base, list);
            }
        }
        if (rowsByBase.size === 0) return;

        for (const groupId in this.groupedAccountsMap) {
            const groupDef = this.groupedAccountsMap[groupId];
            if (groupDef == null || !Array.isArray(groupDef.accounts)) continue;

            let changed = false;
            const next: string[] = [];
            for (const entry of groupDef.accounts) {
                if (MaskingHelper.isRowScopedIdentifier(entry)) {
                    if (!next.includes(entry)) next.push(entry);
                    continue;
                }
                // Only the instances this group could actually hold — same contextual rule
                // `applyGroupingAndSummarize` applies (the group id extends its container's path).
                const instances = (rowsByBase.get(entry) ?? []).filter(
                    (row) => row.containerSortingId !== '' && groupDef.groupId.startsWith(row.containerSortingId),
                );
                if (instances.length < 2) {
                    if (!next.includes(entry)) next.push(entry);
                    continue;
                }
                for (const row of instances) if (!next.includes(row.rowIdentifier)) next.push(row.rowIdentifier);
                changed = true;
            }
            if (changed) groupDef.accounts = next;
        }
    }

    /**
     * REV-DUPLICATE: Walk the VIEW MODEL (balanceSheet / incomeStatement) and assign
     * stable uniqueAccountIds to every instance of (accountNumber, name) that still has no
     * uniqueAccountId. After assigning in the view model, mirrors each ID to the
     * corresponding finStatements account by matching on rowNo (so the ID is
     * included in the next save payload).
     *
     * The view model is a deep clone of finStatements, so we must update BOTH.
     * Walking the view model is the source of truth for what the user sees in the
     * rendered table; walking finStatements (by rowNo) ensures persistence.
     *
     * Returns a list of { rowNo, positionIndex, uniqueAccountId, fliName } so the
     * caller can identify which UUID belongs to the row that was clicked.
     */
    private assignUniqueAccountIds(
        accountNumber: string,
        name: string,
        statement: ReclassificationStatement,
        /**
         * When given, only rows whose FULL identifier equals this are stamped.
         *
         * (accountNumber, name) is not the identity row-scoped keys are built from — that also
         * carries the shareholder. Matching on the pair alone would stamp ids onto
         * `1000 Darlehen - Müller` and `1000 Darlehen - Meier` as well, whose identifiers were
         * never in conflict, and so change the grouping/masking identity of accounts that are not
         * duplicates at all. Callers that know the identity (the duplicate pass) pass it; the
         * reclass-cell path keeps the historical pair-only behaviour.
         */
        baseIdentifier?: string,
    ): Array<{ rowNo: number | undefined; positionIndex: number; uniqueAccountId: string; fliName: string }> {
        const vmRoot = statement === ReclassificationStatement.BALANCE_SHEET
            ? this.balanceSheet : this.incomeStatement;
        const statementIndex = statement === ReclassificationStatement.BALANCE_SHEET ? 0 : 1;
        const fs = this.finStatements?.[statementIndex];

        const assigned: Array<{ rowNo: number | undefined; positionIndex: number; uniqueAccountId: string; fliName: string }> = [];
        let positionIndex = 0;

        // Ids already in use for this (accountNumber, name) — either persisted or handed out earlier
        // in this pass. `makeUid` must never return one of them: two rows sharing an id are, to
        // every consumer, one row.
        const usedUids = new Set<string>(
            this.collectUniqueAccountIds(statement, accountNumber, name, baseIdentifier),
        );

        // Deterministic ID so duplicate refs stay stable across rerenders/reopens.
        // This prevents false "account deleted" conflicts when a transaction
        // points to a duplicate target selected in the modal.
        const makeUid = (acc: any, posIdx: number): string => {
            const statementTag = statement === ReclassificationStatement.BALANCE_SHEET ? 'BS' : 'IS';
            const normalizedName = (name ?? '').trim();
            const stem = `dup-${statementTag}-${accountNumber}-${normalizedName}`;
            const primary = acc?.rowNo != null ? `${stem}-row-${acc.rowNo}` : `${stem}-pos-${posIdx}`;
            if (!usedUids.has(primary)) {
                usedUids.add(primary);
                return primary;
            }

            // The import row is NOT unique: the two legs of a balance-switch account are
            // `cloneDeep` of one account (`BalanceSwitchAccountService.splitAccount`), so both carry
            // the same `rowNo` — and an id derived from it alone made the legs indistinguishable,
            // which is how grouping one leg ejected the other from ITS group.
            //
            // The FIRST claimant keeps the plain id so anything already persisted against it (a
            // reclassification ref, a group membership) still resolves; the collider gets a
            // qualifier. Its own side is the stable choice where there is one, since it survives
            // re-rendering and reloading; otherwise fall back to the position, which is
            // deterministic for a given mapping.
            const side =
                acc?.originsFromAsset === true ? 'asset' : acc?.originsFromAsset === false ? 'liability' : undefined;
            if (side != null && !usedUids.has(`${primary}-${side}`)) {
                usedUids.add(`${primary}-${side}`);
                return `${primary}-${side}`;
            }
            let ordinal = 2;
            while (usedUids.has(`${primary}-${ordinal}`)) ordinal += 1;
            usedUids.add(`${primary}-${ordinal}`);
            return `${primary}-${ordinal}`;
        };

        /** Rows this call is about — see `baseIdentifier`. */
        const isTargetRow = (acc: any): boolean => {
            if (acc == null || acc.accountNumber !== accountNumber || (acc.name ?? '') !== name) return false;
            if (baseIdentifier == null) return true;
            return MaskingHelper.getAccountIdentifier(acc.accountNumber, acc.name ?? '', acc.shareholder) === baseIdentifier;
        };

        // Step 1: walk the VIEW MODEL to assign UUIDs to all matching accounts.
        // We track the FLI name so it can be stored in uniqueAccountIdMeta.
        const maybeAssignVm = (acc: any, fliName: string): void => {
            if (!isTargetRow(acc) || acc.uniqueAccountId) return;
            const uid = makeUid(acc, positionIndex);
            acc.uniqueAccountId = uid;
            assigned.push({ rowNo: acc.rowNo, positionIndex: positionIndex++, uniqueAccountId: uid, fliName });
            // Store label metadata for enriching the target dropdown.
            this.uniqueAccountIdMeta.set(uid, {
                fliName,
                closingBalance: acc.closingBalance ?? 0,
                openingBalance: acc.openingBalance ?? 0,
            });
        };

        const walkVmFli = (fli: any): void => {
            const fliName: string = fli?.name ?? fli?.label ?? '';
            if (Array.isArray(fli?.accounts)) {
                for (const a of fli.accounts) {
                    if (a == null) continue;
                    // Grouped-summary account: recurse into original child accounts
                    // so true duplicates inside grouped rows get UUIDs too.
                    if (a.groupId && Array.isArray(a.accounts) && a.accounts.length > 0) {
                        for (const child of a.accounts) {
                            maybeAssignVm(child, fliName);
                        }
                        continue;
                    }
                    if (a.type === ItemType.SUB_FLI || a.type === ItemType.OF_THAT_ITEM) {
                        if (Array.isArray(a.accounts)) {
                            for (const sa of a.accounts) maybeAssignVm(sa, fliName);
                        }
                    } else {
                        maybeAssignVm(a, fliName);
                    }
                }
            }
        };

        const walkVmGroup = (g: any): void => {
            const itemList = (Array.isArray(g?.sortedItems) ? g.sortedItems : null)
                ?? (Array.isArray(g?.items) ? g.items : null);
            if (itemList) {
                for (const item of itemList) {
                    if (item == null) continue;
                    if (item.type === ItemType.FLI) walkVmFli(item);
                    else if ('sortedItems' in item || 'items' in item) walkVmGroup(item);
                }
            }
            // groups array on the node itself (parallel to sortedItems — both must be walked)
            if (Array.isArray(g?.groups)) { for (const sub of g.groups) walkVmGroup(sub); }
        };

        if (vmRoot != null) walkVmGroup(vmRoot);

        if (assigned.length === 0) return assigned;
        this.pendingUniqueAccountIds = true;

        // Step 2: build a lookup map from rowNo → uuid for the mirror step.
        // Also build an ordered list of UUIDs for accounts without a rowNo so
        // we can fall back to positional matching in the finStatements walk.
        const uidByRowNo = new Map<number, string>();
        const noRowNoUids: string[] = []; // ordered by positionIndex for positional fallback
        const sortedByPosition = [...assigned].sort((a, b) => a.positionIndex - b.positionIndex);
        for (const entry of sortedByPosition) {
            if (entry.rowNo != null) {
                uidByRowNo.set(entry.rowNo, entry.uniqueAccountId);
            } else {
                noRowNoUids.push(entry.uniqueAccountId);
            }
        }

        // Step 3: mirror UUIDs to the raw finStatements so they are included
        // in the next save payload.  Accounts without a rowNo are matched
        // positionally (the Nth such account in finStatements → Nth UUID) —
        // this is safe because finStatements is the source of the deep-clone
        // that produced the view model, so both walk in the same structural
        // order before sorting/grouping is applied.
        //
        // IMPORTANT: this.finStatements comes from getActiveFinStatementsWithReclasses$()
        // (store slice finStatmentsAdjusted, reclass-materialized, for display) while
        // this.annualReport comes from the RAW getActiveAnnualReport$() (store slice
        // ar.finStatements, save-side isolation). These are two DIFFERENT store slices,
        // not raw-vs-bridged of one array. The positional/rowNo mirroring relies on both
        // trees sharing the same imported structure (a pre-existing property that holds
        // independently of this change). getAnnualReportToBeSaved() reads from
        // this.annualReport.finStatements, so we must mirror to BOTH
        // clones to ensure UUIDs survive the save round-trip.
        const mirrorToMapping = (m: any): void => {
            if (m == null) return;
            // Each call gets its own counter so positional matching is correct
            // for the specific object tree being walked.
            let noRowNoPosIdx = 0;
            const mirrorAcc = (acc: any): void => {
                if (!isTargetRow(acc) || acc.uniqueAccountId) return;
                let uid: string | undefined;
                if (acc.rowNo != null) {
                    uid = uidByRowNo.get(acc.rowNo);
                } else {
                    // Positional fallback for accounts without a rowNo.
                    uid = noRowNoUids[noRowNoPosIdx++];
                }
                if (uid) acc.uniqueAccountId = uid;
            };
            const mirrorFli = (fli: any): void => {
                if (Array.isArray(fli?.accounts)) {
                    for (const a of fli.accounts) {
                        if (a == null) continue;
                        // Grouped-summary account: recurse into original child accounts
                        // to mirror the same UUIDs into finStatements payload.
                        if (a.groupId && Array.isArray(a.accounts) && a.accounts.length > 0) {
                            for (const child of a.accounts) { mirrorAcc(child); }
                            continue;
                        }
                        if (a.type === ItemType.SUB_FLI || a.type === ItemType.OF_THAT_ITEM) {
                            if (Array.isArray(a.accounts)) { for (const sa of a.accounts) mirrorAcc(sa); }
                        } else { mirrorAcc(a); }
                    }
                }
            };
            const mirrorGroup = (g: any): void => {
                const itemList = (Array.isArray(g?.sortedItems) ? g.sortedItems : null)
                    ?? (Array.isArray(g?.items) ? g.items : null);
                if (itemList) {
                    for (const item of itemList) {
                        if (item == null) continue;
                        if (item.type === ItemType.FLI) mirrorFli(item);
                        else if ('sortedItems' in item || 'items' in item) mirrorGroup(item);
                    }
                }
                if (Array.isArray(g?.groups)) { for (const sub of g.groups) mirrorGroup(sub); }
            };
            if (m.template?.group != null) mirrorGroup(m.template.group);
            if (Array.isArray(m.accounts)) { for (const a of m.accounts) mirrorAcc(a); }
        };

        // Mirror to this.finStatements (local clone from getActiveFinStatementsWithReclasses$)
        if (fs != null) {
            mirrorToMapping(fs.templateAndAccountsMapping);
        }
        // Mirror to this.annualReport.finStatements (the clone getAnnualReportToBeSaved reads)
        const fsFromAr = this.annualReport?.finStatements?.[statementIndex];
        if (fsFromAr != null) {
            mirrorToMapping(fsFromAr.templateAndAccountsMapping);
        }

        return assigned;
    }

    /** Core logic to open the reclassification modal — called after disambiguation if needed. */
    private openReclassModal(args: {
        accountRef: IReclassificationAccountRef;
        period: ReclassificationPeriod;
        statement: ReclassificationStatement;
    }): void {
        // NOTE: We do NOT pre-assign UUIDs to non-source duplicate pairs here.
        // Doing so would cause ALL duplicate pairs in the statement to receive
        // UUIDs simultaneously on the first modal open, making every pair
        // appear as two distinct entries in the target dropdown and showing
        // enriched (FLI + balance) labels for all of them — even accounts the
        // user hasn't interacted with yet.
        //
        // UUID assignment only happens when the user explicitly clicks on a
        // specific duplicate account (source side: onReclassCellClick; target
        // side: when a duplicate is selected from the dropdown).  Until then,
        // un-disambiguated duplicate pairs merge into a single entry in the
        // dropdown — which is intentional: the user "already knows what they
        // are choosing" (per product requirement).

        const accounts = this.accountsByStatement[args.statement] ?? [];
        const sourceAccount = accounts.find((a) => {
            if (args.accountRef.uniqueAccountId) {
                return (a as any).uniqueAccountId === args.accountRef.uniqueAccountId;
            }
            return a.accountNumber === args.accountRef.accountNumber && (a.name ?? '') === args.accountRef.name;
        });
        // REV-21798: the subtitle shows what the table shows — a renamed account under its display
        // name, and a system-created reclass account as `MAN`, never its internal identity.
        const labelRef = sourceAccount ?? args.accountRef;
        const sourceLabel = `${displayAccountNumber(labelRef.accountNumber)} ${this.getAccountDisplayName(
            labelRef.accountNumber,
            labelRef.name ?? '',
            (labelRef as { shareholder?: string }).shareholder,
            labelRef.uniqueAccountId,
        )}`;

        const sourceBalance: number | null = this.lookupAccountBalance(args.accountRef, args.statement, args.period);

        // Build target options: same statement, exclude source.
        const availableTargets = this.buildReclassTargetOptions(args.accountRef, args.statement);

        const existingOutgoing = getOutgoingTransactions(this.reclassTransactions, args.accountRef, args.period);
        const existingIncoming = getIncomingTransactions(this.reclassTransactions, args.accountRef, args.period);

        this.reclassModalContext = {
            sourceRef: args.accountRef,
            sourceLabel,
            sourceBalance,
            period: args.period,
            statement: args.statement,
            sourceSide: this.sideOfAccount(args.accountRef, args.statement),
            availableTargets,
            existingOutgoing,
            existingIncoming,
        };
        this.reclassModalOpen = true;
    }

    /** Pull the live balance of an account at a given period from the rendered statements. */
    private lookupAccountBalance(
        ref: IReclassificationAccountRef,
        statement: ReclassificationStatement,
        period: ReclassificationPeriod,
    ): number | null {
        const root = statement === ReclassificationStatement.BALANCE_SHEET ? this.balanceSheet : this.incomeStatement;
        if (root == null) return null;
        /** True when account node matches the ref, using uniqueAccountId when present. */
        const matchesRef = (a: any): boolean => {
            if (ref.uniqueAccountId) return a?.uniqueAccountId === ref.uniqueAccountId;
            return a?.accountNumber === ref.accountNumber && (a?.name ?? '') === ref.name;
        };
        /**
         * REV-21798: a reclassification account has no imported balance — its
         * entire value IS its net reclass. Reading only
         * `calculatedClosingBalance` would report 0 and make the modal's
         * "exceeds balance" warning fire on every amount once such an account
         * is used as a source. Reclass-inclusive value only for these rows;
         * imported accounts keep the previous semantics so the warning's
         * behaviour there is unchanged.
         */
        const balanceOf = (a: any): number | null => {
            if (a?.isReclassAccount) {
                return period === ReclassificationPeriod.CURRENT_YEAR ? this.finalCY(a) : this.finalPY(a);
            }
            return period === ReclassificationPeriod.CURRENT_YEAR
                ? (a.calculatedClosingBalance ?? a.closingBalance ?? null)
                : (a.calculatedOpeningBalance ?? a.openingBalance ?? null);
        };

        const visit = (g: any): number | null => {
            const itemList = (Array.isArray(g?.sortedItems) ? g.sortedItems : null) ?? (Array.isArray(g?.items) ? g.items : null);
            if (itemList) {
                for (const item of itemList) {
                    if (item == null) continue;
                    if (item.type === ItemType.FLI && Array.isArray(item.accounts)) {
                        for (const a of item.accounts) {
                            if (a == null) continue;
                            if (matchesRef(a)) {
                                return balanceOf(a);
                            }
                            if (a.type === ItemType.SUB_FLI || a.type === ItemType.OF_THAT_ITEM) {
                                if (Array.isArray(a.accounts)) {
                                    for (const sa of a.accounts) {
                                        if (matchesRef(sa)) {
                                            return balanceOf(sa);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            if (Array.isArray(g?.groups)) {
                for (const sub of g.groups) {
                    const v = visit(sub);
                    if (v != null) return v;
                }
            }
            return null;
        };
        return visit(root);
    }

    onReclassModalSave(payload: ReclassModalSavePayload): void {
        // REV-21798: resolve every container target into a concrete account
        // BEFORE anything reaches the store. From here on the transaction list
        // is account → account exactly as it has always been.
        const resolvedEntries = payload.entries.map((e) => ({
            id: e.id,
            target: this.materializeReclassTarget(e.target, payload.sourceRef, payload.statement),
            amount: e.amount,
            description: e.description,
        }));

        this.reclassStore.upsertForSource(
            payload.sourceRef,
            payload.period,
            payload.statement,
            resolvedEntries,
        );
        // REV-20899: after the store update, evict any session-pending phantom
        // reclasses accounts that are now unreferenced (the view-model prune
        // in initializeFinStatements handles persisted ones; here we clean up
        // the session-only list so it stays consistent).
        this.evictOrphanedPendingReclassesAccounts();
        // A container target mutates the grouping map (a generated account joins a group, and a
        // NEW_GROUP draft creates one), so the map has to reach `groupingAndMaskingToBeSaved`.
        this.persistGroupingAndMasking();
        this.reclassModalOpen = false;
        this.reclassModalContext = null;
        // Trigger engine recalculation so derived reclass values refresh.
        this.renderTable();
    }

    /**
     * REV-21798: turn a modal target selection into a concrete account ref.
     *
     * Accounts pass straight through. A container (FLI / SubFLI / G&M group)
     * gets a system-created reclassification account:
     *
     *   number  a generated unique `MAN-xxxxxxxx` (displayed as `MAN`)
     *   name    `Umgliederung aus <source account name>`
     *
     * The `(host, name)` pair is the reuse key: reclassifying the same source
     * into the same container again — a second entry, or the other period —
     * lands on the one account rather than piling up duplicates. REV-21881: the
     * host includes the selected G&M GROUP, not just the FLI/SubFLI. Two groups
     * of one FLI are two containers, so each gets its own account; keying the
     * reuse on the FLI alone made the second reclassification adopt the first
     * group's account and the amount stayed in that group (the one rendered
     * above) instead of moving to the group the user picked. That is also
     * why the name is NOT collapsed when the source is itself a reclass
     * account: `Umgliederung aus Umgliederung aus X` is long, but it keeps a
     * direct hop and a hop-via-intermediate distinguishable, which a collapsed
     * name would silently merge.
     */
    private materializeReclassTarget(
        selection: ReclassTargetSelection,
        sourceRef: IReclassificationAccountRef,
        statement: ReclassificationStatement,
    ): IReclassificationAccountRef {
        if (selection.kind === 'ACCOUNT') return selection.ref;

        // The name follows the report's language. Reuse, however, is matched language-AGNOSTICALLY
        // (`matchesSystemReclassAccountName`): an account generated while the report was German
        // must still be recognised when the same reclassification is repeated in English, or the
        // check would miss and silently create a duplicate. When an existing account is reused its
        // OWN persisted name is returned — never the freshly translated one — because the
        // transaction ref has to resolve against the account as it is actually stored.
        // Named after the source's DISPLAY name, so a renamed source produces
        // "<prefix> <new name>" rather than resurrecting the original import name the user has
        // already replaced. Reuse still matches the original too (below), so renaming a source
        // does not orphan the account generated from it before the rename.
        const sourceDisplayName = this.getAccountDisplayName(
            sourceRef.accountNumber,
            sourceRef.name,
            undefined,
            sourceRef.uniqueAccountId,
        );
        const hostFliId = selection.hostFliId;
        const hostSubFliId = selection.hostSubFliId;
        // REV-21881: part of the container identity, not just decoration — see the reuse-key note
        // above. `undefined` means the target is the FLI/SubFLI itself, i.e. NOT inside any group.
        // A NEW_GROUP draft has no group yet — this is the moment it becomes one, because the user has
        // now confirmed the reclassification (the dialog that described it changed nothing).
        const hostGroupId =
            selection.kind === 'NEW_GROUP' ? this.createGroupForDraft(selection, statement) : selection.groupId;
        if (selection.kind === 'NEW_GROUP' && hostGroupId == null) {
            // The FLI vanished between describing the group and saving; fall back to the FLI-less
            // path rather than inventing a container.
            return this.materializeReclassTarget(
                { ...selection, kind: 'FLI', groupId: undefined },
                sourceRef,
                statement,
            );
        }

        // The CONTAINER the user picked goes into the name — a group, an FLI or a SubFLI — so several
        // reclassifications from one source into DIFFERENT containers are distinguishable in the
        // target dropdown, and everywhere else the account is listed, instead of all reading
        // "Umgliederung aus <source>". `label` is the caption the dropdown showed; `groupName` is the
        // one a not-yet-created group carries.
        //
        // ⚠️ RESOLVED FIRST, then named — the order is the fix, not a tidy-up. `createGroupForDraft`
        // matches an existing caption IGNORING CASE (`findGroupIdByCaption`), so drafting
        // "sammelgruppe" onto an FLI that already holds "Sammelgruppe" correctly reclassifies into the
        // EXISTING group. Naming from `selection` before that ran took the string the user typed, so
        // one group ended up holding an account labelled "… nach sammelgruppe" — a caption that
        // contradicts the group it sits in. The surviving group's stored `groupName` is the caption
        // that is actually on screen, so that is the one the name snapshots.
        //
        // The name is a SNAPSHOT (see `systemReclassAccountName`): renaming the container or moving
        // the account later does not rewrite it, because the name is load-bearing identity. The user
        // renames the account themselves if they want it to follow.
        const resolvedGroupCaption = hostGroupId != null ? this.groupedAccountsMap[hostGroupId]?.groupName : undefined;
        const containerCaption = resolvedGroupCaption ?? selection.groupName ?? selection.label ?? '';
        const name = systemReclassAccountName(
            sourceDisplayName,
            this.reclassGeneratedAccountNamePrefix,
            containerCaption,
            this.reclassGeneratedAccountNameConnector,
        );

        // 1. Reuse a session-pending account created earlier in this session.
        const pending = this.pendingNewReclassesAccounts.find(
            (p) =>
                p.statement === statement &&
                p.fliId === hostFliId &&
                (p.subFliId ?? undefined) === hostSubFliId &&
                (p.groupId ?? undefined) === hostGroupId &&
                matchesSystemReclassAccountName(p.name, sourceDisplayName, sourceRef.name),
        );
        if (pending != null) {
            this.joinReclassAccountToGroup(pending.accountNumber, pending.name, hostGroupId);
            return { accountNumber: pending.accountNumber, name: pending.name };
        }

        // 2. Reuse one already persisted in this container.
        const persisted = this.findPersistedReclassAccount(
            statement,
            hostFliId,
            hostSubFliId,
            hostGroupId,
            sourceDisplayName,
            sourceRef.name,
        );
        if (persisted != null) {
            this.joinReclassAccountToGroup(persisted.accountNumber, persisted.name, hostGroupId);
            return { accountNumber: persisted.accountNumber, name: persisted.name };
        }

        // 3. Create a new one.
        const existingAccountNumbers = this.reclassExistingAccountNumbers;
        // Ensure uniqueness across numbers created earlier in this session (and even within one modal save).
        for (const p of this.pendingNewReclassesAccounts) {
            if (p?.accountNumber != null) existingAccountNumbers.add(String(p.accountNumber).trim());
        }
        const accountNumber = generateSystemReclassAccountNumber(existingAccountNumbers);
        this.pendingNewReclassesAccounts.push({
            statement,
            fliId: hostFliId,
            subFliId: hostSubFliId,
            groupId: hostGroupId,
            accountNumber,
            name,
        });
        this.joinReclassAccountToGroup(accountNumber, name, hostGroupId);
        return { accountNumber, name };
    }

    /**
     * Turn a `NEW_GROUP` draft into a real group definition and return its id.
     *
     * Called from `materializeReclassTarget`, i.e. while SAVING the reclassification — never while the
     * dialog is open. A name already used in that FLI resolves to the same id (container + name), so
     * the reclassification joins that group instead of producing a second one under one caption, which
     * is the rule `onGroupSave` follows.
     */
    private createGroupForDraft(
        selection: Extract<ReclassTargetSelection, { kind: Exclude<ReclassTargetKind, 'ACCOUNT'> }>,
        statement: ReclassificationStatement,
    ): string | null {
        const groupName = (selection.groupName ?? selection.label ?? '').trim();
        if (!groupName) return null;
        const root =
            statement === ReclassificationStatement.BALANCE_SHEET ? this.balanceSheet : this.incomeStatement;
        const fli = root != null ? this.findFliById(root, selection.hostFliId) : null;
        const containerSortingId: string | undefined = fli?.sortingId;
        if (!containerSortingId) return null;

        // One caption, one group. The id is derived from `sanitizeId(name)`, which is
        // case-SENSITIVE, and a group that was RENAMED keeps the id built from its original name —
        // so deriving blindly would put a second "Alte Gruppe" next to the first whenever the
        // casing differed or the group had been renamed. Resolve against the captions that exist in
        // this container first, exactly as the dialog's hint promises.
        const existingGroupId = this.findGroupIdByCaption(containerSortingId, groupName);
        if (existingGroupId != null) return existingGroupId;

        const groupId = `${containerSortingId}__group__${this.sanitizeId(groupName)}`;
        if (this.groupedAccountsMap[groupId] == null) {
            this.groupedAccountsMap[groupId] = { groupName, groupId, accounts: [] };
        }
        return groupId;
    }

    /**
     * The id of the group in `containerSortingId` whose caption is `groupName`, ignoring case and
     * surrounding whitespace — or null when that container has no such group.
     *
     * Compares the stored `groupName` rather than re-deriving an id, because the two can disagree:
     * `onGroupSave`'s rename branch changes the caption and deliberately keeps the id.
     */
    private findGroupIdByCaption(containerSortingId: string, groupName: string): string | null {
        const wanted = groupName.trim().toLowerCase();
        if (!wanted) return null;
        // A SubFLI's groups live under `…__subfli__X__group__…`, so this prefix cannot match them.
        const prefix = `${containerSortingId}__group__`;
        for (const groupId of Object.keys(this.groupedAccountsMap)) {
            if (!groupId.startsWith(prefix)) continue;
            const caption = (this.groupedAccountsMap[groupId]?.groupName ?? '').trim().toLowerCase();
            if (caption === wanted) return groupId;
        }
        return null;
    }

    /**
     * REV-21798: add a generated reclass account to a G&M group definition, so
     * it renders inside that group (and is summarized into it) exactly like a
     * manually grouped imported account.
     *
     * REV-21881: an account may only ever belong to ONE group — the rule
     * `onGroupSave` and `dropIntoGroup` already apply — so it is dropped from
     * every other group first. Without that, an account being moved stayed in
     * its previous group's definition while also joining the new one, and
     * `applyGroupingAndSummarize` handed it to whichever group renders first,
     * i.e. the one ABOVE. When the target is a plain FLI / SubFLI (`groupId`
     * undefined) the account belongs in no group at all, which is the same
     * removal without a re-add.
     */
    private joinReclassAccountToGroup(accountNumber: string, name: string, groupId?: string): void {
        const identifier = MaskingHelper.getAccountIdentifier(accountNumber, name, undefined);
        this.removeAccountFromAllGroups(identifier, groupId);
        if (!groupId) return;
        const groupDef = this.groupedAccountsMap[groupId];
        if (groupDef == null) return;
        if (!groupDef.accounts.includes(identifier)) {
            groupDef.accounts.push(identifier);
        }
    }

    /**
     * REV-21798: look for an already-generated reclass account for `sourceAccountName` inside the
     * given container. Reads the view model, where phantoms have been hoisted into `accounts` and
     * flagged `isReclassAccount`.
     *
     * Matches any of the SOURCE names rather than a literal generated name, so an account created
     * in another report language — or before the source was renamed — is still found; the caller
     * reuses the returned (persisted) name verbatim.
     *
     * REV-21881: `hostGroupId` narrows the search to the container the user actually picked —
     * either ONE G&M group's members, or the FLI's/SubFLI's own rows when it is undefined. It used
     * to search the FLI as a whole, groups included, so a reclassification onto a group adopted an
     * account belonging to a DIFFERENT group and the amount stayed there.
     */
    private findPersistedReclassAccount(
        statement: ReclassificationStatement,
        hostFliId: string,
        hostSubFliId: string | undefined,
        hostGroupId: string | undefined,
        ...sourceAccountNames: string[]
    ): { accountNumber: string; name: string } | null {
        const root = statement === ReclassificationStatement.BALANCE_SHEET ? this.balanceSheet : this.incomeStatement;
        if (root == null) return null;

        const matchIn = (container: any): { accountNumber: string; name: string } | null => {
            for (const a of Array.isArray(container?.accounts) ? container.accounts : []) {
                // A grouped-summary node hides its members in `accounts`. Only the SELECTED
                // group's members are candidates; another group's account is another container's.
                if (a?.groupId && Array.isArray(a.accounts)) {
                    if (hostGroupId == null || a.groupId !== hostGroupId) continue;
                    for (const child of a.accounts) {
                        if (
                            child?.isReclassAccount &&
                            matchesSystemReclassAccountName(child.name ?? '', ...sourceAccountNames)
                        ) {
                            return { accountNumber: child.accountNumber, name: child.name ?? '' };
                        }
                    }
                    continue;
                }
                // A row directly under the container — a candidate only when the container ITSELF
                // is the target, otherwise the amount would land outside the selected group.
                if (hostGroupId != null) continue;
                if (a?.isReclassAccount && matchesSystemReclassAccountName(a.name ?? '', ...sourceAccountNames)) {
                    return { accountNumber: a.accountNumber, name: a.name ?? '' };
                }
            }
            return null;
        };

        const isGroup = (item: any): boolean => item != null && 'sortedItems' in item;
        let found: { accountNumber: string; name: string } | null = null;

        const walkGroup = (g: any): void => {
            if (found != null) return;
            const itemList =
                (Array.isArray(g?.sortedItems) ? g.sortedItems : null) ?? (Array.isArray(g?.items) ? g.items : null);
            if (itemList) {
                for (const item of itemList) {
                    if (found != null) return;
                    if (item == null) continue;
                    if (item.type === ItemType.FLI) {
                        if (item.name !== hostFliId) continue;
                        if (hostSubFliId == null) {
                            found = matchIn(item);
                        } else {
                            for (const child of Array.isArray(item.accounts) ? item.accounts : []) {
                                if (
                                    (child?.type === ItemType.SUB_FLI || child?.type === ItemType.OF_THAT_ITEM) &&
                                    child.name === hostSubFliId
                                ) {
                                    found = matchIn(child);
                                    break;
                                }
                            }
                        }
                    } else if (isGroup(item)) {
                        walkGroup(item);
                    }
                }
            }
            if (Array.isArray(g?.groups)) {
                for (const sub of g.groups) walkGroup(sub);
            }
        };

        walkGroup(root);
        return found;
    }

    onReclassModalCancel(): void {
        this.reclassModalOpen = false;
        this.reclassModalContext = null;
    }

    onCreateAccountRequested(event: {
        statement: ReclassificationStatement;
        onCreated: (ref: IReclassificationAccountRef) => void;
        onSelected: (selection: ReclassTargetSelection) => void;
    }): void {
        // The callback is kept for whichever branch the user picks: both end with a concrete account
        // ref being selected in the entry (a group's ref is the `MAN` account created inside it).
        this.newAccountModalStatement = event.statement;
        this.newAccountModalCallback = event.onCreated;
        this.newTargetSelectionCallback = event.onSelected;
        // Measure BEFORE opening, while the reclassification dialog is still rendered.
        this.nestedReclassDialogHeight = this.measureReclassDialogHeight();
        this.newTargetChoiceOpen = true;
    }

    /**
     * The rendered height of the reclassification dialog, or null when it cannot be measured.
     *
     * Read from the DOM because the height is content-driven (entries, the incoming section, the
     * warnings) and capped at 60vh by `.reclass-modal-scroll-body` — there is no value in the
     * component to derive it from. `ham-modal` renders in place, so the dialog box is the
     * `.ham-modal-inner` inside the modal component's own host element.
     */
    private measureReclassDialogHeight(): number | null {
        const inner = document.querySelector('aumico-reclassification-modal .ham-modal-inner');
        const height = inner instanceof HTMLElement ? inner.getBoundingClientRect().height : 0;
        return height > 0 ? Math.round(height) : null;
    }

    /**
     * True while the chooser or one of the creation dialogs is open.
     *
     * The reclassification dialog is HIDDEN (not destroyed) for that stretch, so the user never sees
     * one dialog stacked on another. Hiding rather than closing keeps the modal's component alive —
     * a destroyed one would re-initialise its entries from `existingOutgoing` and lose the amount
     * typed so far.
     */
    public get isNestedReclassDialogOpen(): boolean {
        return this.newTargetChoiceOpen || this.newAccountModalOpen || this.newGroupModalOpen;
    }

    /**
     * Whether a row may be reclassified at all.
     *
     * An account with no number is a COMPUTED row — the transferred annual result
     * ("Jahresgewinn /-verlust", created with `accountNumber: ''`), not a posting account. The
     * reclassification engine resolves every transaction ref by account number (+ name), so such a
     * row could neither be addressed reliably as a source nor as a target; the cell is therefore not
     * rendered at all and `buildReclassTargetOptions` leaves it out of the target list.
     */
    public canReclassAccount(account: { accountNumber?: string | null } | null | undefined): boolean {
        return (account?.accountNumber ?? '').trim().length > 0;
    }

    /** Step 2 of the "+" flow: open the form for the chosen kind. */
    onNewTargetChoice(kind: ReclassNewTargetKind): void {
        this.newTargetChoiceOpen = false;
        if (kind === 'GROUP') {
            this.newGroupModalOpen = true;
        } else {
            this.newAccountModalOpen = true;
        }
        this.cdr.markForCheck();
    }

    onNewTargetChoiceCancelled(): void {
        this.newTargetChoiceOpen = false;
        this.nestedReclassDialogHeight = null;
        this.newAccountModalCallback = null;
        this.newTargetSelectionCallback = null;
    }

    /**
     * Create a GROUP as the reclassification target.
     *
     * Deliberately ends in the same state as picking an existing group from the target dropdown: the
     * group is created in the chosen FLI and a `MAN` reclassification account is materialized inside
     * it, which is what the entry then points at. So there is one mechanism for "reclassify into a
     * group", and this dialog only spares the user a trip to the table to create the group first.
     *
     * A name already used in that FLI resolves to the SAME group id (container + name), so the
     * reclassification joins the existing group instead of producing a second one under one caption —
     * the rule `onGroupSave` follows. The dialog says so before saving.
     */
    onNewGroupCreated(event: {
        groupName: string;
        fliId: string;
        statement: ReclassificationStatement;
    }): void {
        const context = this.reclassModalContext;
        const root =
            event.statement === ReclassificationStatement.BALANCE_SHEET ? this.balanceSheet : this.incomeStatement;
        const fli = root != null ? this.findFliById(root, event.fliId) : null;
        // No context or no resolvable FLI means there is nothing to attach the group to.
        if (context == null || fli == null || !fli.sortingId) {
            this.newGroupModalOpen = false;
            this.newAccountModalCallback = null;
            this.newTargetSelectionCallback = null;
            return;
        }

        // NOTHING is created here. The dialog only records WHAT to create: the entry points at a
        // draft, and `materializeReclassTarget` turns it into a real group with its `MAN` account when
        // the modal is saved. So the table behind the modal does not change until the user confirms,
        // and cancelling leaves no group and no account behind.
        const selection: ReclassTargetSelection = {
            kind: 'NEW_GROUP',
            // Unique per (FLI, name) so the option value is stable and two drafts cannot collide.
            containerId: `newgroup|${event.fliId}|${this.sanitizeId(event.groupName)}`,
            label: event.groupName,
            hostFliId: event.fliId,
            groupName: event.groupName,
        };

        // Offer the draft in the dropdown, so the entry shows what it is pointing at.
        this.reclassModalContext = {
            ...context,
            availableTargets: [
                ...context.availableTargets.filter((option) => option.value !== targetSelectionKey(selection)),
                {
                    value: targetSelectionKey(selection),
                    label: this.reclassNewGroupOptionLabel(event.groupName),
                    statement: event.statement,
                    selection,
                },
            ],
        };
        if (this.newTargetSelectionCallback) this.newTargetSelectionCallback(selection);

        this.newGroupModalOpen = false;
        this.newAccountModalCallback = null;
        this.newTargetSelectionCallback = null;
        this.cdr.markForCheck();
    }

    /** Dropdown label for a not-yet-created group, e.g. "Dialog Gruppe (neue Gruppe)". */
    private reclassNewGroupOptionLabel(groupName: string): string {
        const suffix = this.i18nextService.t('aumico_grouping_masking_reclass_new_group_option_suffix') as string;
        return suffix ? `${groupName} ${suffix}` : groupName;
    }

    onNewGroupCancelled(): void {
        this.newGroupModalOpen = false;
        this.nestedReclassDialogHeight = null;
        this.newAccountModalCallback = null;
        this.newTargetSelectionCallback = null;
    }

    /**
     * Group names already in use, per FLI id — so the group dialog can point out that a name is
     * taken (the reclassification then lands in that group rather than a new one).
     */
    public get reclassExistingGroupNamesByFli(): Record<string, string[]> {
        const out: Record<string, string[]> = {};
        const stmt = this.newAccountModalStatement ?? this.currentReclassStatement;
        const root = stmt === ReclassificationStatement.BALANCE_SHEET ? this.balanceSheet : this.incomeStatement;
        if (root == null) return out;
        for (const option of this.reclassNewAccountFliOptions) {
            const fli = this.findFliById(root, option.id);
            const containerSortingId: string | undefined = fli?.sortingId;
            if (!containerSortingId) continue;
            const names: string[] = [];
            for (const groupId of Object.keys(this.groupedAccountsMap)) {
                if (!groupId.startsWith(`${containerSortingId}__group__`)) continue;
                const groupName = this.groupedAccountsMap[groupId]?.groupName;
                if (groupName) names.push(groupName);
            }
            out[option.id] = names;
        }
        return out;
    }

    onNewAccountCreated(event: {
        ref: IReclassificationAccountRef;
        fliId: string;
        statement: ReclassificationStatement;
    }): void {
        // REV-20899: park the new phantom on `pendingNewReclassesAccounts`
        // (session-scoped). It will be:
        //   • injected into the cloned view model in `initializeFinStatements`
        //     so the new row appears immediately under its FLI;
        //   • merged into a cloned copy of `finStatements` in
        //     `getAnnualReportToBeSaved` so it is persisted as a regular
        //     `IReclassesAccounts` entry on the AR;
        //   • cleared from this list in `onSaveSuccessfully` — from then on
        //     it lives entirely on `finStatements...fli.reclassesAccounts`
        //     and follows the same path as any existing reclasses account.
        //
        // The raw `this.finStatements` / `this.annualReport` references are
        // never mutated. That keeps `getActiveFinStatementsWithReclasses$()`
        // and the NgRx store safe from side effects.
        if (event.fliId) {
            const alreadyRegistered = this.pendingNewReclassesAccounts.some(
                (p) =>
                    p.statement === event.statement &&
                    p.fliId === event.fliId &&
                    p.accountNumber === event.ref.accountNumber &&
                    p.name === event.ref.name,
            );
            if (!alreadyRegistered) {
                this.pendingNewReclassesAccounts.push({
                    statement: event.statement,
                    fliId: event.fliId,
                    accountNumber: event.ref.accountNumber,
                    name: event.ref.name,
                });
            }
        }

        // Re-render so the new phantom row appears in the FLI's account list
        // and so `accountsByStatement` picks it up.
        this.renderTable();

        // Call back into the modal so the new ref auto-selects in the entry.
        if (this.newAccountModalCallback) this.newAccountModalCallback(event.ref);
        // Refresh the modal context's availableTargets so the new account appears.
        if (this.reclassModalContext != null) {
            const srcRef = this.reclassModalContext.sourceRef;
            this.reclassModalContext = {
                ...this.reclassModalContext,
                availableTargets: this.buildReclassTargetOptions(srcRef, this.reclassModalContext.statement),
            };
        }
        this.newAccountModalOpen = false;
        this.newAccountModalCallback = null;
        this.cdr.markForCheck();
    }

    onNewAccountCancelled(): void {
        this.newAccountModalOpen = false;
        this.newAccountModalCallback = null;
    }

    /** Find an FLI in the current statement by its `name` (used as id in the dropdown). */
    private findFliById(group: IAccountMappingTemplateGroup, fliId: string): any | null {
        const g = group as any;
        const itemList = (Array.isArray(g?.sortedItems) ? g.sortedItems : null) ?? (Array.isArray(g?.items) ? g.items : null);
        if (itemList) {
            for (const item of itemList) {
                if (item != null && item.type === ItemType.FLI && (item as any).name === fliId) return item;
            }
        }
        if (Array.isArray(g?.groups)) {
            for (const sub of g.groups) {
                const f = this.findFliById(sub, fliId);
                if (f) return f;
            }
        }
        return null;
    }


    /** Conflict view toggles. */
    toggleReclassConflictsView(): void {
        this.reclassConflictsViewOpen = !this.reclassConflictsViewOpen;
    }

    /**
     * REV-20899: toggle the previous-year reclass lock. When ON, PY reclass
     * cells are read-only — `onReclassCellClick` short-circuits on PY and
     * the cells themselves render in disabled state. The flag is persisted
     * on `IGroupingMasking.reclassPyLocked` via `persistGroupingAndMasking`.
     */
    public toggleReclassPyLock(): void {
        this.reclassPyLocked = !this.reclassPyLocked;
        // Roll the change into the to-be-saved snapshot immediately so the
        // unsaved-changes banner fires and the next save persists the lock.
        this.persistGroupingAndMasking();
        this.cdr.markForCheck();
    }

    onOpenReclassPreview(): void {
        this.reclassPreviewOpen = true;
    }

    onCloseReclassPreview(): void {
        this.reclassPreviewOpen = false;
    }

    onReclassConflictDelete(ref: IReclassificationAccountRef): void {
        this.reclassStore.deleteByAccount(ref);
        // REV-20899: clean up orphaned phantom accounts after conflict deletion.
        this.evictOrphanedPendingReclassesAccounts();
        this.renderTable();
    }

    onReclassConflictReassign(event: { oldRef: IReclassificationAccountRef; newRef: IReclassificationAccountRef }): void {
        this.reclassStore.reassignAnyLeg(event.oldRef, event.newRef);
        this.renderTable();
    }

    getReclassAccountsByStatement(): Record<ReclassificationStatement, Array<{ accountNumber: string; name: string; uniqueAccountId?: string }>> {
        return this.accountsByStatement;
    }

    /**
     * REV-20899: Build a Set of composite keys (`accountNumber|name`) for every
     * account referenced as SOURCE or TARGET in the current transaction list.
     * Used to detect orphaned reclasses accounts.
     */
    public static buildReferencedReclassKeys(
        transactions: IReclassificationTransaction[],
        pendingNewReclassesAccounts: IReclassificationAccountRef[] = [],
    ): Set<string> {
        return GroupingMaskingHelper.buildReferencedReclassKeys(transactions, pendingNewReclassesAccounts);
    }

    /**
     * The keys the TRANSACTION LIST references — nothing else.
     *
     * Kept separate from {@link buildReferencedReclassKeys}, which additionally shields
     * session-pending accounts from the render-time prune. Deciding whether an account is orphaned
     * must NOT consult that shield: an account that vouches for its own survival can never be
     * cleaned up, which is precisely why a `MAN` account outlived the deletion of its last
     * transaction.
     */
    private buildTransactionReclassKeys(): Set<string> {
        // Passing no pending list is what makes this the transactions-only set — the helper adds
        // session-pending accounts only when it is handed them.
        return GroupingMaskingHelper.buildReferencedReclassKeys(this.reclassTransactions);
    }

    /**
     * REV-20899: Remove from `pendingNewReclassesAccounts` any phantom entry
     * that is no longer referenced by any transaction in the store.
     * Persisted reclasses accounts (already in finStatements) are handled
     * by the `pruneOrphanedReclassesAccounts` call inside initializeFinStatements.
     */
    private evictOrphanedPendingReclassesAccounts(): void {
        if (this.pendingNewReclassesAccounts.length === 0) return;
        // Transactions ONLY — see buildTransactionReclassKeys. Asking the render-time set here made
        // this a tautology (every pending account is in it), so a `MAN` account created in this
        // session survived the deletion of the very transaction it was created for.
        const referenced = this.buildTransactionReclassKeys();
        const survivors: INewReclassesAccountSessionEntry[] = [];
        for (const pending of this.pendingNewReclassesAccounts) {
            if (referenced.has(`${pending.accountNumber}|${pending.name}`)) {
                survivors.push(pending);
                continue;
            }
            // The account is going away, so its group membership has to go with it — otherwise a
            // group created for a reclassification the user then abandoned keeps a member that no
            // longer exists (and `removeAccountFromAllGroups` deletes the definition once it empties,
            // which is exactly what should happen to such a group).
            this.removeAccountFromAllGroups(
                MaskingHelper.getAccountIdentifier(pending.accountNumber, pending.name, undefined),
            );
        }
        this.pendingNewReclassesAccounts = survivors;
    }

    /** Resolver passed to the modal so each entry's target side can be looked up. */
    public readonly reclassTargetSideResolver = (ref: IReclassificationAccountRef): 'AKTIV' | 'PASSIV' | null => {
        return this.reclassModalContext
            ? this.sideOfAccount(ref, this.reclassModalContext.statement)
            : null;
    };

    /**
     * Set of every account number that currently exists in *either* statement.
     *
     * The new-account-nested modal uses this to reject any account number
     * collision — whether the collision is with a regular imported account,
     * a sub-FLI account, a group account, or a phantom reclass account. The
     * downstream mapping/save flow treats `accountNumber` as the primary
     * identifier, so the new account's number must be globally unique even if
     * the user is creating it under a different FLI/statement than the
     * existing match.
     */
    public get reclassExistingAccountNumbers(): Set<string> {
        const set = new Set<string>();
        const all = [
            ...(this.accountsByStatement[ReclassificationStatement.BALANCE_SHEET] ?? []),
            ...(this.accountsByStatement[ReclassificationStatement.INCOME_STATEMENT] ?? []),
        ];
        for (const a of all) {
            if (a?.accountNumber != null) set.add(String(a.accountNumber).trim());
        }
        return set;
    }

    /**
     * Builds the FLI dropdown options for the nested new-account modal.
     *
     * Lists every FLI of the *currently active statement* (the tab the user
     * is on when they open the modal), so a phantom reclass account created
     * on the BS tab can only be attached to a BS FLI and vice-versa. The
     * option `id` is the FLI's `name` (matches the same identifier used by
     * `findFliById` to insert the phantom on save), and the `label` is the
     * FLI caption shown to the user.
     */
    public get reclassNewAccountFliOptions(): Array<{ id: string; label: string }> {
        const stmt = this.newAccountModalStatement ?? this.currentReclassStatement;
        const root = stmt === ReclassificationStatement.BALANCE_SHEET ? this.balanceSheet : this.incomeStatement;
        const out: Array<{ id: string; label: string }> = [];
        if (root == null) return out;
        // FLIs can be nested inside sub-groups that live in `sortedItems`
        // (not just `groups`) once `generateAndAddFullSortingState` has run.
        // Recurse into both.
        const isGroup = (item: any): boolean => item != null && 'sortedItems' in item;
        const walk = (g: any): void => {
            const itemList = (Array.isArray(g?.sortedItems) ? g.sortedItems : null) ?? (Array.isArray(g?.items) ? g.items : null);
            if (itemList) {
                for (const item of itemList) {
                    if (item == null) continue;
                    if (item.type === ItemType.FLI) {
                        const id = (item as any).name;
                        if (id) {
                            out.push({
                                id,
                                label: (item as any).caption ?? (item as any).name ?? '',
                            });
                        }
                    } else if (isGroup(item)) {
                        walk(item);
                    }
                }
            }
            if (Array.isArray(g?.groups)) {
                for (const sub of g.groups) walk(sub);
            }
        };
        walk(root);
        return out;
    }

    private initTexts(): void {
        this.aumico_templates_group_account_mapping_of_that_item_text = this.i18nextService.t(
            'aumico_templates_group_account_mapping_of_that_item_text',
        );
        this.aumico_annual_report_import_form_balance_title = this.i18nextService.t(
            'aumico_annual_report_import_form_balance_title',
        );
        this.aumico_annual_report_import_form_income_title = this.i18nextService.t(
            'aumico_annual_report_import_form_income_title',
        );
        this.tabsTitles = [
            this.aumico_annual_report_import_form_balance_title,
            this.aumico_annual_report_import_form_income_title,
        ];
        this.aumico_grouping_masking_define_bsa = this.i18nextService.t('aumico_grouping_masking_define_bsa');
        this.aumico_grouping_masking_remove_bsa = this.i18nextService.t('aumico_grouping_masking_remove_bsa');
        this.aumico_grouping_masking_ungroup = this.i18nextService.t('aumico_grouping_masking_ungroup');
        this.aumico_grouping_masking_remove_from_group = this.i18nextService.t(
            'aumico_grouping_masking_remove_from_group',
        );
        this.aumico_grouping_masking_rename_modal_title = this.i18nextService.t(
            'aumico_grouping_masking_rename_modal_title',
        );
        this.aumico_grouping_masking_rename_modal_text = this.i18nextService.t(
            'aumico_grouping_masking_rename_modal_text',
        );
        this.aumico_grouping_masking_rename_modal_label_original = this.i18nextService.t(
            'aumico_grouping_masking_rename_modal_label_original',
        );
        this.aumico_grouping_masking_rename_modal_label_new = this.i18nextService.t(
            'aumico_grouping_masking_rename_modal_label_new',
        );
        this.aumico_grouping_masking_rename_modal_label_placeholder = this.i18nextService.t(
            'aumico_grouping_masking_rename_modal_label_placeholder',
        );
        this.aumico_grouping_masking_rename_modal_label_delete = this.i18nextService.t(
            'aumico_grouping_masking_rename_modal_label_delete',
        );
        this.aumico_grouping_masking_grouping_modal_title = this.i18nextService.t(
            'aumico_grouping_masking_grouping_modal_title',
        );
        this.aumico_grouping_masking_grouping_modal_text = this.i18nextService.t(
            'aumico_grouping_masking_grouping_modal_text',
        );
        this.aumico_grouping_masking_grouping_modal_label_placeholder = this.i18nextService.t(
            'aumico_grouping_masking_grouping_modal_label_placeholder',
        );
        this.aumico_grouping_masking_modal_apply = this.i18nextService.t('aumico_grouping_masking_modal_apply');
        this.aumico_grouping_masking_modal_cancel = this.i18nextService.t('aumico_grouping_masking_modal_cancel');
        this.aumico_grouping_masking_modal_confirm = this.i18nextService.t('aumico_grouping_masking_modal_confirm');
        this.aumico_grouping_masking_modal_dismiss = this.i18nextService.t('aumico_grouping_masking_modal_dismiss');
        this.aumico_grouping_masking_split_modal_title = this.i18nextService.t(
            'aumico_grouping_masking_split_modal_title',
        );
        this.aumico_grouping_masking_split_modal_title_start = this.i18nextService.t(
            'aumico_grouping_masking_split_modal_title_start',
        );
        this.aumico_grouping_masking_split_modal_title_output = this.i18nextService.t(
            'aumico_grouping_masking_split_modal_title_output',
        );
        this.aumico_grouping_masking_split_modal_title_counterpart = this.i18nextService.t(
            'aumico_grouping_masking_split_modal_title_counterpart',
        );
        this.aumico_grouping_masking_split_modal_title_counterpart_placeholder = this.i18nextService.t(
            'aumico_grouping_masking_split_modal_title_counterpart_placeholder',
        );
        this.confirmActionText = this.aumico_grouping_masking_modal_confirm;
        this.reclassAccountBadgeTooltip = this.i18nextService.t(
            'aumico_grouping_masking_reclass_account_badge_tooltip',
        ) as string;
        this.reclassGeneratedAccountNamePrefix = this.i18nextService.t(
            'aumico_grouping_masking_reclass_generated_account_name_prefix',
        ) as string;
        this.reclassGeneratedAccountNameConnector = this.i18nextService.t(
            'aumico_grouping_masking_reclass_generated_account_name_to',
            { defaultValue: SYSTEM_RECLASS_ACCOUNT_NAME_CONNECTOR },
        ) as string;
        this.aumico_grouping_masking_remove_original_name = function (accountName: string) {
            return this.i18nextService.t('aumico_grouping_masking_remove_original_name', {
                replace: { value: accountName },
            });
        };
        this.aumico_grouping_masking_confirm_ungroup_text = function (value: string) {
            return this.i18nextService.t('aumico_grouping_masking_confirm_ungroup_text', { replace: { value: value } });
        };
        this.aumico_grouping_masking_confirm_unsplitt_text = function (value: string) {
            return this.i18nextService.t('aumico_grouping_masking_confirm_unsplitt_text', {
                replace: { value: value },
            });
        };
    }
    /**
     * Handles the processing of financial statements once received.
     */
    private initializeFinStatements(finStatements: IFinStatements[]): void {
        if (!finStatements || finStatements.length < 2 || finStatements[0].templateAndAccountsMapping?.template == null || finStatements[1].templateAndAccountsMapping?.template == null) {
            // Handle insufficient financial statements case
            return;
        }
        // 1. Deep Clone Input Data to create a separate View Model
        const bs = GroupingMaskingComponent.deepClone(finStatements[0].templateAndAccountsMapping.template.group);
        const is = GroupingMaskingComponent.deepClone(finStatements[1].templateAndAccountsMapping.template.group);

        // 1b. REV-21798: inject session-pending phantom reclasses accounts, GC the
        //     orphaned ones, then hoist every phantom into its host's `accounts`.
        //
        //     Ordering matters and is the reason this block sits at the very top.
        //     Steps 2-7 below (path generation, grouping, splitting, sorting,
        //     summaries) all operate exclusively on `accounts`. Until REV-21798
        //     phantoms were injected AFTER all of them, which is why a manual
        //     account had no `sortingId`, could not join a group, could not be
        //     ordered and broke checkbox selection. Injecting and hoisting first
        //     makes a phantom indistinguishable from an imported account for the
        //     whole rest of the pipeline.
        //
        //     Prune runs BEFORE the hoist, while entries are still on
        //     `reclassesAccounts` where `pruneOrphanedReclassesAccounts` expects
        //     them. `applyNewReclassesAccounts` is idempotent, so re-injecting
        //     after a save round-trip cannot duplicate a row.
        if (this.pendingNewReclassesAccounts.length > 0) {
            GroupingHelper.applyNewReclassesAccounts(
                bs,
                this.pendingNewReclassesAccounts,
                ReclassificationStatement.BALANCE_SHEET,
            );
            GroupingHelper.applyNewReclassesAccounts(
                is,
                this.pendingNewReclassesAccounts,
                ReclassificationStatement.INCOME_STATEMENT,
            );
        }

        const referencedKeysView = GroupingMaskingComponent.buildReferencedReclassKeys(
            this.reclassTransactions,
            this.pendingNewReclassesAccounts,
        );
        GroupingHelper.pruneOrphanedReclassesAccounts(bs, referencedKeysView);
        GroupingHelper.pruneOrphanedReclassesAccounts(is, referencedKeysView);

        // REV-21798: accounts deleted in this session are still present in the
        // unsaved `finStatements` this view model is cloned from, so they have
        // to be dropped here too — otherwise the row reappears on the next
        // render. The prune above will not catch them while an incoming
        // transaction keeps them referenced.
        if (this.deletedReclassAccounts.length > 0) {
            const deletedKeys = new Set(
                this.deletedReclassAccounts.map((ref) => `${ref.accountNumber}|${ref.name ?? ''}`),
            );
            GroupingHelper.removeReclassesAccounts(bs, deletedKeys);
            GroupingHelper.removeReclassesAccounts(is, deletedKeys);
        }

        GroupingHelper.hoistReclassesAccounts(bs);
        GroupingHelper.hoistReclassesAccounts(is);

        this.balanceSheet = GroupingPathHelper.generateAndAddFullSortingState(bs, '');
        this.incomeStatement = GroupingPathHelper.generateAndAddFullSortingState(is, '');

        // 2. Ensure all accounts arrays are initialized (empty list handling)
        GroupingMaskingComponent.ensureAccountsArrays(this.balanceSheet);
        GroupingMaskingComponent.ensureAccountsArrays(this.incomeStatement);

        // 2b. Give every TRUE DUPLICATE row its own identity, and rewrite legacy blanket group
        //     membership into per-row entries — BEFORE grouping, splitting, sorting and masking
        //     read those identifiers below.
        this.ensureDuplicateRowIdentities();

        // 3. Apply grouping information
        this.balanceSheet = GroupingHelper.applyGroupingAndSummarize(this.balanceSheet, this.groupedAccountsMap);
        this.incomeStatement = GroupingHelper.applyGroupingAndSummarize(this.incomeStatement, this.groupedAccountsMap);

        // 4. apply switch account
        this.balanceSheet = SplitAccountHelper.applySplitAccounts(this.balanceSheet, this.splitGroupAccountsMap);

        // 5. Apply any previously saved sorting state (now loaded into this.sortingState)
        GroupingMaskingComponent.applySavedSorting(this.balanceSheet, this.sortingState);
        GroupingMaskingComponent.applySavedSorting(this.incomeStatement, this.sortingState);

        // 6. Finally recalcualte summaries after all manipulations
        this.balanceSheet = GroupingHelper.recalculateGroupSummaries(this.balanceSheet);
        this.incomeStatement = GroupingHelper.recalculateGroupSummaries(this.incomeStatement);

        // 7. REV-20899: write per-account closingReClass/openingReClass from
        //    the transaction list, then aggregate them up the tree so
        //    `finalCY(item) / finalPY(item)` works on every row.
        GroupingMaskingComponent.applyTransactionalReclassesToViewModel(this.balanceSheet, this.reclassTransactions);
        GroupingMaskingComponent.applyTransactionalReclassesToViewModel(this.incomeStatement, this.reclassTransactions);

        debug('Balance Sheet View Model:', this.balanceSheet);
    }

    /**
     * REV-20899: walk the view model, derive each leaf account's
     * `closingReClass` / `openingReClass` from `this.reclassTransactions` (by
     * composite-key lookup), then bottom-up aggregate the sums onto group /
     * FLI / SubFLI / section nodes.
     */
    public static applyTransactionalReclassesToViewModel(
        root: IAccountMappingTemplateGroup | undefined,
        transactions: IReclassificationTransaction[],
    ): void {
        if (root == null) return;
        const setLeaf = (acc: any): void => {
            if (acc == null) return;
            const ref: IReclassificationAccountRef = {
                accountNumber: acc.accountNumber,
                name: acc.name ?? '',
                uniqueAccountId: acc.uniqueAccountId,
            };
            acc.closingReClass = getReclassSumForRef(
                transactions,
                ref,
                ReclassificationPeriod.CURRENT_YEAR,
            );
            acc.openingReClass = getReclassSumForRef(
                transactions,
                ref,
                ReclassificationPeriod.PREVIOUS_YEAR,
            );
        };

        const traverse = (item: any): { cy: number; py: number } => {
            if (item == null) return { cy: 0, py: 0 };

            // Leaf account: derive directly from transactions.
            if (item.accountNumber != null && !item.sortedItems && !item.accounts) {
                setLeaf(item);
                return { cy: item.closingReClass ?? 0, py: item.openingReClass ?? 0 };
            }

            // Grouped account: leaf for transactions (group itself), but also
            // contains a list of `accounts` children that have their own refs.
            // Treat the grouped-account node as a leaf for the transaction
            // lookup AND recurse into its children so their own reclass values
            // also resolve (the user can reclass individual accounts inside a
            // group).
            let cySum = 0;
            let pySum = 0;
            if (item.accountNumber != null && Array.isArray(item.accounts)) {
                setLeaf(item);
                cySum += item.closingReClass ?? 0;
                pySum += item.openingReClass ?? 0;
                for (const child of item.accounts) {
                    const r = traverse(child);
                    cySum += r.cy;
                    pySum += r.py;
                }
                // Group summary rows display `finalCY/finalPY(acc)`, so they must
                // carry the full aggregate reclass total (own + children).
                item.closingReClass = cySum;
                item.openingReClass = pySum;
                return { cy: cySum, py: pySum };
            }

            // Pure aggregate node (FLI, group, section): sum children.
            if (Array.isArray(item.sortedItems)) {
                for (const child of item.sortedItems) {
                    if (child?.type === 'SUB_TOTAL') continue;
                    const r = traverse(child);
                    cySum += r.cy;
                    pySum += r.py;
                }
            } else if (Array.isArray(item.accounts)) {
                for (const child of item.accounts) {
                    const r = traverse(child);
                    cySum += r.cy;
                    pySum += r.py;
                }
            }
            // FLIs and SubFLIs can also carry synthetic reclassesAccounts that
            // a transaction may reference — include them in the aggregate.
            if (Array.isArray(item.reclassesAccounts)) {
                for (const ra of item.reclassesAccounts) {
                    const r = traverse(ra);
                    cySum += r.cy;
                    pySum += r.py;
                }
            }
            item.closingReClass = cySum;
            item.openingReClass = pySum;
            return { cy: cySum, py: pySum };
        };

        traverse(root);
    }

    resetGrouping(): void {
        this.groupedAccountsMap = {};
        this.splitGroupAccountsMap = {};
        this.sortingState = {};
        this.hiddenSubtotalsSet = new Set();
        this.renderTable();
    }

    renderTable(): void {
        // Implementation for rendering the table goes here
        this.initializeFinStatements(this.finStatements);

        // ========================================
        // NEW: Collect drop list IDs grouped by parent FLI
        // ========================================
        this.collectDropListIdsByFli();

        // REV-20899: rebuild the per-statement account index so reclass cells
        // and conflict detection work with the latest view models.
        this.rebuildReclassAccountIndex();

        // 🚀 **ADD THIS LINE**
        this.cdr.markForCheck();
    }

    /** REV-20899: which statement the user is currently viewing (BS vs ER). */
    public get currentReclassStatement(): ReclassificationStatement {
        return this.selectedTab === 0
            ? ReclassificationStatement.BALANCE_SHEET
            : ReclassificationStatement.INCOME_STATEMENT;
    }

    // ===========================================
    // --- NEW: DROP LIST ID COLLECTION BY FLI ---
    // ===========================================

    /**
     * Collects drop list IDs grouped by their parent FLI.
     * This ensures items can only be moved within the same FLI context.
     */
    private collectDropListIdsByFli(): void {
        this.dropListIdsByFli = new Map();

        if (this.balanceSheet) {
            this.collectDropListIdsFromGroup(this.balanceSheet, 'bs');
        }
        if (this.incomeStatement) {
            this.collectDropListIdsFromGroup(this.incomeStatement, 'is');
        }

        debug('Collected drop list IDs by FLI:', Object.fromEntries(this.dropListIdsByFli));
    }

    /**
     * Recursively collects drop list IDs from a group structure.
     */
    private collectDropListIdsFromGroup(group: IAccountMappingTemplateGroup | any, parentPath: string): void {
        if (!group?.sortedItems) return;

        for (const item of group.sortedItems) {
            if (this.isGroup(item)) {
                // Nested group - recurse
                const groupPath = this.getGroupPath(parentPath, item);
                this.collectDropListIdsFromGroup(item, groupPath);
            } else if (item.type === ItemType.FLI && this.hasFliContent(item)) {
                // FLI - this is the parent container
                const fliPath = this.getFliPath(parentPath, item);

                // Initialize the array for this FLI with itself
                const fliDropListIds: string[] = [fliPath];

                // Collect IDs from accounts within this FLI
                if (item.accounts) {
                    for (const acc of item.accounts) {
                        // Group accounts have their own drop list
                        if (
                            acc.groupId &&
                            acc.accounts?.length > 0 &&
                            acc.type !== 'SUB FLI' &&
                            acc.type !== 'OF_THAT_ITEM'
                        ) {
                            fliDropListIds.push(acc.groupId);
                        }
                        // Sub-FLIs have their own drop list
                        if ((acc.type === 'SUB FLI' || acc.type === 'OF_THAT_ITEM') && acc.accounts?.length > 0) {
                            const subFliPath = this.getSubFliPath(fliPath, acc);
                            fliDropListIds.push(subFliPath);
                        }
                    }
                }

                // Store all IDs for this FLI context
                this.dropListIdsByFli.set(fliPath, fliDropListIds);

                // Also map each child ID back to the same array for easy lookup
                for (const childId of fliDropListIds) {
                    if (childId !== fliPath) {
                        this.dropListIdsByFli.set(childId, fliDropListIds);
                    }
                }
            }
        }
    }

    /**
     * Gets the connected drop list IDs for a given drop list.
     * Returns only IDs within the same FLI context.
     */
    public getConnectedDropLists(dropListId: string): string[] {
        return this.dropListIdsByFli.get(dropListId) || [dropListId];
    }

    // ===========================================
    // --- UNIQUE PATH GENERATORS ---
    // ===========================================
    // UPDATED: Now delegation to the helper ensures the ID generation is
    // identical between template rendering and state saving.

    /**
     * Generates a unique path for a Group by combining parent path with group's title and rowNo
     */
    public getGroupPath(parentPath: string, group: any): string {
        return GroupingPathHelper.getGroupPath(parentPath, group);
    }

    /**
     * Generates a unique path for an FLI by combining parent path with FLI's name and rowNo
     */
    public getFliPath(parentPath: string, fli: any): string {
        return GroupingPathHelper.getFliPath(parentPath, fli);
    }

    /**
     * Generates a unique path for a Sub-FLI
     */
    public getSubFliPath(parentFliPath: string, subFli: any): string {
        return GroupingPathHelper.getSubFliPath(parentFliPath, subFli);
    }

    /**
     * Sanitizes a string to be safe for use as an ID (removes special characters)
     */
    public sanitizeId(str: string): string {
        return GroupingPathHelper.sanitizeId(str);
    }

    // ===========================================
    // --- VISIBILITY CHECKS ---
    // ===========================================

    /**
     * FIX #2: Checks if an FLI has any accounts (including sub-FLIs with accounts)
     * Returns true if the FLI should be displayed
     *
     * REV-20899: also returns true if the FLI carries any phantom reclasses
     * accounts on `reclassesAccounts`. Without this, an FLI whose only
     * content is a user-created phantom (no imported accounts) would be
     * hidden by `*ngIf="hasFliContent(item)"` in the template and the new
     * row would never appear under the selected FLI.
     */
    public hasFliContent(fli: any): boolean {
        if (Array.isArray(fli?.reclassesAccounts) && fli.reclassesAccounts.length > 0) {
            return true;
        }
        if (!fli.accounts || fli.accounts.length === 0) {
            return false;
        }

        // Check if there are any regular accounts or sub-FLIs with content
        return fli.accounts.some((acc: any) => {
            if (acc.type === 'SUB FLI' || acc.type === 'OF_THAT_ITEM') {
                // Sub-FLI must have accounts to be considered as content
                return acc.accounts && acc.accounts.length > 0;
            }
            // Regular account counts as content
            return true;
        });
    }

    /**
     * FIX #3: Checks if a group has any visible content (sortedItems with content)
     * Returns true if the group should be displayed
     */
    public hasGroupContent(group: any): boolean {
        if (!group.sortedItems || group.sortedItems.length === 0) {
            return false;
        }

        return group.sortedItems.some((item: any) => {
            if (this.isGroup(item)) {
                // Nested group - recursively check if it has content
                return this.hasGroupContent(item);
            } else if (item.type === ItemType.FLI) {
                // FLI - check if it has accounts
                return this.hasFliContent(item);
            } else {
                // Headers, Subtotals - only show if there's other content
                // We'll handle this by checking siblings
                return false;
            }
        });
    }

    // ===========================================
    // --- TRACKBY FUNCTIONS ---
    // ===========================================

    /**
     * TrackBy function for groups - uses title + rowNo for uniqueness
     * Arrow function to preserve 'this' context
     */
    public trackByGroup = (index: number, item: any): string => {
        if (this.isGroup(item)) {
            return `group_${item.title}_${item.rowNo || index}`;
        }
        // For leaf items (FLI, Header, etc.)
        return `item_${item.type}_${item.name || item.caption}_${item.rowNo || index}`;
    };

    /**
     * TrackBy function for accounts - uses accountNumber or name for uniqueness
     * Arrow function to preserve 'this' context
     */
    public trackByAccount = (index: number, acc: any): string => {
        if (acc.type === 'SUB FLI' || acc.type === 'OF_THAT_ITEM') {
            return `subfli_${acc.name || acc.caption}_${acc.rowNo || index}`;
        }
        // FIX: Use consistent identifier for trackBy
        const identifier = this.getIdentifier(acc);
        return `acc_${identifier || index}`;
    };

    // ===========================================
    // --- COLLAPSE STATE MANAGEMENT ---
    // ===========================================

    /**
     * Checks if a group is collapsed
     */
    public isCollapsed(groupPath: string): boolean {
        return this.collapseState.has(groupPath);
    }

    /**
     * Toggles the collapse state of a group
     */
    public toggleCollapse(groupPath: string): void {
        if (this.collapseState.has(groupPath)) {
            this.collapseState.delete(groupPath);
        } else {
            this.collapseState.add(groupPath);
        }
        // Persist to IndexedDB
        this.persistCollapseState();
    }

    // ===========================================
    // --- TAB NAVIGATION ---
    // ===========================================

    public onTabClick(item: NavigationItem): void {
        const index = this.navigationItems.findIndex((nav) => nav.name === item.name);
        if (index !== -1) {
            this.navigationItems.forEach((nav, i) => {
                nav.active = i === index;
            });
            this.selectedTab = index;
        }
    }

    // ===========================================
    // --- DRAG & DROP ---
    // ===========================================

    /**
     * Helper to distinguish Groups from Items in Template
     */
    public isGroup(item: any): boolean {
        return GroupingMaskingComponent.isGroupNode(item);
    }

    public static isGroupNode(item: any): boolean {
        return GroupingMaskingHelper.isGroupNode(item);
    }

    /**
     * REV-21342: SubFLI / Of-That rows are STRUCTURE, defined and ordered in the Account-Mapping
     * step. They are excluded from every sorting mechanism in this step (manual drag, the Sort
     * button, and saved sorting state) — only standard accounts may be re-ordered here.
     */
public static isStructuralRow(item: any): boolean {
    return GroupingMaskingHelper.isStructuralRow(item);
}

    /**
     * Sorts only the ACCOUNT entries of a FLI child list with `compare`, keeping structural rows
     * (SubFLIs / Of-That items) pinned at their template-defined positions.
     */
    public static sortPreservingStructuralRows(list: any[], compare: (a: any, b: any) => number): void {
        GroupingMaskingHelper.sortPreservingStructuralRows(list, compare);
    }

    /**
     * Handles the Drop Event for FLI and Sub-FLI containers
     * @param event The CDK Drag Drop event
     * @param list The actual array in the View Model to be reordered
     * @param containerId The unique path of the FLI/Sub-FLI containing these items
     */
    public drop(event: CdkDragDrop<any[]>, list: any[], containerId: string): void {
        if (!list) {
            return;
        }

        // REV-21342: structural rows (SubFLIs / Of-That items) are not sortable here — their
        // order comes from Account Mapping. Their cdkDrag is disabled in the template; this
        // guard also protects against programmatic/edge-case drops.
        if (GroupingMaskingComponent.isStructuralRow(event.item?.data)) {
            return;
        }

        if (event.previousContainer === event.container) {
            // Same container - just reorder
            if (event.previousIndex !== event.currentIndex) {
                moveItemInArray(list, event.previousIndex, event.currentIndex);
            }
        } else {
            // Different containers - transfer the item
            const movedItem = event.item.data;

            // Don't allow moving groups between containers via this handler
            if (movedItem.groupId && movedItem.accounts) {
                debug('Cannot move group accounts between FLIs via drop()');
                return;
            }

            // Remove from previous container
            const prevIndex = event.previousContainer.data.indexOf(movedItem);
            if (prevIndex > -1) {
                event.previousContainer.data.splice(prevIndex, 1);
            }

            // Add to new container at the correct index
            list.splice(event.currentIndex, 0, movedItem);

            // If the item was part of a group, remove it from the group definition
            if (movedItem.accountNumber) {
                // FIX: Use full Identifier for removal
                const identifier = this.getIdentifier(movedItem);
                this.removeAccountFromAllGroups(identifier, undefined, movedItem.sortingId);
            }
        }

        // Store the new order in our separate sorting layer
        // FIX: Map using full Identifiers for Sorting State
        // REV-21342: structural rows are excluded — their order is the mapping-defined template
        // order and must never be captured in (or replayed from) the sorting state.
        this.sortingState[containerId] = list
            .filter((item) => !GroupingMaskingComponent.isStructuralRow(item))
            .map((item) => this.getIdentifier(item));

        // Persist state to the backend database (asynchronously)
        this.persistGroupingAndMasking();
        this.cdr.markForCheck();
    }

    /**
     * Handles dropping an account INTO a group
     * @param event The CDK Drag Drop event
     * @param targetGroupAccount The group account receiving the dropped item
     */
    public dropIntoGroup(event: CdkDragDrop<any[]>, targetGroupAccount: IGroupedAccount): void {

        // clear the selection to avoid issues
        this.clearSelection();
        const movedItem = event.item.data;

        // 1. Check if we are dropping the group onto itself (ignore)
        if (movedItem === targetGroupAccount) {
            debug('Cannot drop group onto itself');
            return;
        }

        // 2. Don't allow dropping groups into other groups
        if (movedItem.groupId && movedItem.accounts) {
            debug('Cannot drop a group into another group');
            return;
        }

        // 3. Same container - just reorder within the group
        if (event.previousContainer === event.container) {
            if (event.previousIndex !== event.currentIndex) {
                moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
            }
        } else {
            // 4. Different containers - transfer the item into the group
            debug('Dropping account into group:', targetGroupAccount.groupName);
            debug('Item dropped:', movedItem);

            // Remove from previous container
            const prevIndex = event.previousContainer.data.indexOf(movedItem);
            if (prevIndex > -1) {
                event.previousContainer.data.splice(prevIndex, 1);
            } else {
                // Fallback: use the previousIndex from the event
                event.previousContainer.data.splice(event.previousIndex, 1);
            }

            // Add to target group's accounts array
            event.container.data.splice(event.currentIndex, 0, movedItem);

            // Update the groupedAccountsMap - add account to this group
            const groupDef = this.groupedAccountsMap[targetGroupAccount.groupId];
            if (groupDef && movedItem.accountNumber) {
                // FIX: Calculate Identifier once
                const identifier = this.getIdentifier(movedItem);

                // First remove from any other groups — of this account's own container only.
                this.removeAccountFromAllGroups(identifier, undefined, movedItem.sortingId);

                // Then add to this group
                if (!groupDef.accounts.includes(identifier)) {
                    groupDef.accounts.push(identifier);
                }
            }
        }

        // Persist the changes
        this.persistGroupingAndMasking();
        this.renderTable();
        this.cdr.markForCheck();
    }

    /**
     * Helper to remove an account from group definitions.
     *
     * REV-21881: `exceptGroupId` keeps ONE group untouched, for callers that are moving an account
     * INTO that group. Skipping it matters: an account that is already the group's only member
     * would otherwise empty the definition, which deletes the group (below) — and the re-add then
     * finds nothing to add to.
     *
     * `containerSortingId` limits the removal to groups of the account's OWN container, which is the
     * same rule reads apply (`applyGroupingAndSummarize` only matches a group whose id extends the
     * container's path). Without it the rule "an account may only ever belong to one group" reached
     * across containers, and since two rows can share an identifier — the two legs of a
     * balance-switch account are one account split into an asset FLI and a liability FLI — grouping
     * one leg ejected the other from ITS group. Omit it to sweep every group (used where the caller
     * has no container at hand and the identifier is unique by construction, e.g. a generated `MAN`
     * account).
     */
    private removeAccountFromAllGroups(
        identifier: string,
        exceptGroupId?: string,
        containerSortingId?: string,
    ): void {
        for (const groupId in this.groupedAccountsMap) {
            if (groupId === exceptGroupId) continue;
            if (containerSortingId != null && containerSortingId !== '' && !groupId.startsWith(containerSortingId)) {
                continue;
            }
            if (Object.prototype.hasOwnProperty.call(this.groupedAccountsMap, groupId)) {
                const groupDef = this.groupedAccountsMap[groupId];
                // FIX: indexOf now correctly searches for the full Identifier string
                const index = groupDef.accounts.indexOf(identifier);
                if (index > -1) {
                    groupDef.accounts.splice(index, 1);
                    // If group is now empty, remove it
                    if (groupDef.accounts.length === 0) {
                        delete this.groupedAccountsMap[groupId];
                    }
                }
            }
        }
    }

    public createDropPredicate(targetContainerId: string): (item: CdkDrag<any>) => boolean {
        return (item: CdkDrag<any>): boolean => {
            const draggedItem = item.data;

            // Don't allow dropping groups (grouped accounts)
            if (
                draggedItem.groupId &&
                Array.isArray(draggedItem.accounts) &&
                draggedItem.type !== 'SUB FLI' &&
                draggedItem.type !== 'OF_THAT_ITEM'
            ) {
                return false;
            }

            // Allow sub-FLIs to be dropped only into the parent FLI
            if (draggedItem.type === 'SUB FLI' || draggedItem.type === 'OF_THAT_ITEM') {
                if (!draggedItem.sortingId) {
                    return false;
                }

                // Only allow dropping into the parent FLI
                // The Sub-FLI's sortingId contains the parent FLI path as a prefix.
                // We check if the targetContainerId (the drop list ID) matches that prefix.
                // We also check startsWith to verify the target matches the exact parent path segment.
                return draggedItem.sortingId.startsWith(targetContainerId);
            }

            // For regular accounts, check if they belong to the same FLI
            const draggedItemSortingId = draggedItem.sortingId;
            if (!draggedItemSortingId) {
                return false;
            }

            // Check if dragged item is from a sub-FLI
            const draggedFromSubFli = draggedItemSortingId.includes('__subfli__');
            const targetIsSubFli = targetContainerId.includes('__subfli__');

            // If dragged from sub-FLI, can only drop into same sub-FLI
            if (draggedFromSubFli) {
                if (!targetIsSubFli) {
                    return false; // Can't drop sub-FLI account into main FLI
                }
                // Must be the same sub-FLI
                return draggedItemSortingId === targetContainerId;
            }

            // If dragged from main FLI, can't drop into sub-FLI
            if (!draggedFromSubFli && targetIsSubFli) {
                return false;
            }

            // Same FLI check for regular accounts
            return (
                targetContainerId.startsWith(draggedItemSortingId) ||
                draggedItemSortingId.startsWith(targetContainerId.split('__group__')[0])
            );
        };
    }
    private dragExpandState: Map<string, boolean> = new Map();

    public onGroupDragStarted(groupId: string): void {
        // Remember if it was expanded
        this.dragExpandState.set(groupId, !this.isCollapsed(groupId));

        // Collapse it
        if (!this.isCollapsed(groupId)) {
            this.collapseState.add(groupId);
            this.cdr.markForCheck();
        }
    }

    public onGroupDragEnded(groupId: string): void {
        // Restore previous state if it was expanded
        if (this.dragExpandState.get(groupId)) {
            this.collapseState.delete(groupId);
            this.cdr.markForCheck();
        }
        this.dragExpandState.delete(groupId);
    }
    /**
     * Predicate to allow dropping into FLI containers.
     * Returns true if the item being dragged is NOT a grouped account.
     */
    public canDropIntoFli = (item: CdkDrag<any>): boolean => {
        const data = item.data;
        // Don't allow dropping groups (items with groupId and accounts array)
        // But DO allow regular accounts
        const isGroup = data.groupId && Array.isArray(data.accounts) && data.accounts.length > 0;
        return !isGroup;
    };

    /**
     * Predicate to allow dropping into Group containers.
     * Returns true if the item being dragged is a regular account (not a group itself).
     */
    public canDropIntoGroup = (item: CdkDrag<any>): boolean => {
        const data = item.data;
        // Only allow regular accounts (not groups or sub-FLIs)
        const isGroup = data.groupId && Array.isArray(data.accounts);
        const isSubFli = data.type === 'SUB FLI' || data.type === 'OF_THAT_ITEM';
        return !isGroup && !isSubFli;
    };

    // ===========================================
    // --- PERSISTENCE LOGIC (Split) ---
    // ===========================================

    // --- SORTING (Backend Persistence) ---

    /**
     * Loads the sorting state synchronously from the Annual Report object.
     */
    private loadGroupingAndMaskingFromAR(groupingAndMasking: IGroupingMasking | null): void {
        // REV-20899: hydrate the reclass store from the persisted transactions.
        this.reclassificationsVersion = groupingAndMasking?.reclassificationsVersion;
        this.reclassStore.init(groupingAndMasking?.reclassifications ?? []);

        // A legacy report (reclassificationsVersion !== 1) that already carries
        // legacy reclass values keeps the OLD tab as its reclass editor, so we
        // disable the new feature here. Computed from the whole AR (works even when
        // groupingAndMasking is null, e.g. imported legacy reports).
        this.reclassFeatureDisabled = !isReclassNewWorld(this.annualReport);

        if (groupingAndMasking) {
            this.sortingState = groupingAndMasking.sorting || {};
            this.maskedNamesMap = groupingAndMasking.renamedAccounts || {};
            this.groupedAccountsMap = groupingAndMasking.groupedAccounts || {};
            this.splitGroupAccountsMap = groupingAndMasking.splitGroupAccounts || {};
            this.hiddenSubtotalsSet = new Set(groupingAndMasking.hiddenSubtotals || []);
            // REV-20899: phantoms are persisted on `finStatements...fli.reclassesAccounts`,
            // NOT on `IGroupingMasking`. The session list starts empty — any
            // previously-saved phantoms come back via the AR's finStatements
            // and render automatically through `fliRowEntries(item)`.
            this.pendingNewReclassesAccounts = [];
            this.deletedReclassAccounts = [];
            // REV-20899: hydrate the PY-reclass lock. Default is LOCKED
            // (PY reclasses non-editable); they become editable only when the
            // report was explicitly unlocked (reclassPyLocked === false).
            this.reclassPyLocked = groupingAndMasking.reclassPyLocked !== false;

            // Store original data for change detection
            this.originalGroupingAndMasking = {
                sorting: { ...groupingAndMasking.sorting },
                renamedAccounts: { ...groupingAndMasking.renamedAccounts },
                groupedAccounts: { ...groupingAndMasking.groupedAccounts },
                splitGroupAccounts: { ...groupingAndMasking.splitGroupAccounts },
                hiddenSubtotals: [...(groupingAndMasking.hiddenSubtotals || [])],
                enabled: groupingAndMasking.enabled,
                reclassPyLocked: groupingAndMasking.reclassPyLocked !== false,
                reclassifications: groupingAndMasking.reclassifications ?? [],
                reclassificationsVersion: groupingAndMasking.reclassificationsVersion,
            };

            // IMPORTANT: Also initialize groupingAndMaskingToBeSaved with the same data
            // This prevents false "data changed" detection on initial load
            this.groupingAndMaskingToBeSaved = {
                sorting: { ...groupingAndMasking.sorting },
                renamedAccounts: { ...groupingAndMasking.renamedAccounts },
                groupedAccounts: { ...groupingAndMasking.groupedAccounts },
                splitGroupAccounts: { ...groupingAndMasking.splitGroupAccounts },
                hiddenSubtotals: [...(groupingAndMasking.hiddenSubtotals || [])],
                enabled: groupingAndMasking.enabled,
                reclassPyLocked: groupingAndMasking.reclassPyLocked !== false,
                reclassifications: groupingAndMasking.reclassifications ?? [],
                reclassificationsVersion: groupingAndMasking.reclassificationsVersion,
            };

            this.cdr.markForCheck();
        } else {
            this.sortingState = {};
            this.maskedNamesMap = {};
            this.groupedAccountsMap = {};
            this.splitGroupAccountsMap = {};
            this.hiddenSubtotalsSet = new Set();
            this.pendingNewReclassesAccounts = [];
            this.deletedReclassAccounts = [];
            this.reclassPyLocked = true;
            this.originalGroupingAndMasking = null;
            this.groupingAndMaskingToBeSaved = null;
            this.cdr.markForCheck();
        }
    }

    /**
     * Persists the current sorting state to the Annual Report object via the provider.
     * Assumes AnnualReportProvider has a method to update the groupingAndMasking field.
     */
    private persistGroupingAndMasking(): void {
        if (!this.annualReportId) return;

        // 1. REGENERATE COMPLETE STATE
        // This ensures that the sortingState map matches the current View Model structure exactly.
        // Even if we missed a path update somewhere, this recursively catches it.
        const bsState = this.balanceSheet ? GroupingPathHelper.generateFullSortingState(this.balanceSheet, '') : {};
        const isState = this.incomeStatement
            ? GroupingPathHelper.generateFullSortingState(this.incomeStatement, '')
            : {};

        this.sortingState = {
            ...this.sortingState,
            ...bsState,
            ...isState,
        };

        const reclassifications = this.reclassStore.snapshotForSave();
        // Only mark the report as the transactional (v1) model when it actually
        // carries reclass transactions, or is already v1. NEVER auto-upgrade a
        // legacy (v0) report on an empty list — that would flip a legacy report
        // into the new world and let the derive engine zero its legacy
        // reclasses. Write-once-upward: an existing v1 is preserved.
        const reclassificationsVersion: 0 | 1 | undefined =
            this.reclassificationsVersion === 1 || reclassifications.length > 0
                ? 1
                : this.reclassificationsVersion;

        const groupingAndMasking: IGroupingMasking = {
            sorting: this.sortingState,
            renamedAccounts: this.maskedNamesMap,
            enabled: true,
            groupedAccounts: this.groupedAccountsMap,
            splitGroupAccounts: this.splitGroupAccountsMap,
            hiddenSubtotals: Array.from(this.hiddenSubtotalsSet),
            // REV-20899: persist the transactional reclassifications + version flag.
            // Phantoms themselves are NOT stored here — they ride on
            // `finStatements...fli.reclassesAccounts` via `getAnnualReportToBeSaved`.
            reclassifications,
            reclassificationsVersion,
            // REV-20899: persist the PY-reclass lock so the read-only state
            // survives save/reload.
            reclassPyLocked: this.reclassPyLocked,
        };

        this.groupingAndMaskingToBeSaved = groupingAndMasking;
    }

    // --- ANNUAL REPORT SAVE OVERRIDES ---

    proceedButtonClicked = (): void => {
        this.save();
    };

    override save(): Observable<void> {
        this.persistGroupingAndMasking();
        this.mixpanelService.track(MIXPANEL_EVENTS.UPLOAD_SHEET_TIME_SPENT_TILL_COMPLETION);
        this.isSaveRequestInProgress = true;
        const annualReportToBeSaved = this.getAnnualReportToBeSaved();
        if (annualReportToBeSaved == null) {
            this.onSaveFailed();
            return of(void 0);
        }
        this.dispatchSaveAnnualReport(annualReportToBeSaved);
        return of(void 0);
    }

    protected onSaveSuccessfully(): void {
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(true);
        this.fireSuccessNotification();

        // Update original data to match saved data (no longer "changed")
        if (this.groupingAndMaskingToBeSaved) {
            this.originalGroupingAndMasking = {
                sorting: { ...this.groupingAndMaskingToBeSaved.sorting },
                renamedAccounts: { ...this.groupingAndMaskingToBeSaved.renamedAccounts },
                groupedAccounts: { ...this.groupingAndMaskingToBeSaved.groupedAccounts },
                splitGroupAccounts: { ...this.groupingAndMaskingToBeSaved.splitGroupAccounts },
                hiddenSubtotals: [...(this.groupingAndMaskingToBeSaved.hiddenSubtotals || [])],
                enabled: this.groupingAndMaskingToBeSaved.enabled,
                reclassPyLocked: this.groupingAndMaskingToBeSaved.reclassPyLocked === true,
                reclassifications: this.groupingAndMaskingToBeSaved.reclassifications ?? [],
                reclassificationsVersion: this.groupingAndMaskingToBeSaved.reclassificationsVersion,
            };
        }
        // REV-20899: the just-saved AR will re-emit from the store with the
        // session phantoms baked into `finStatements...fli.reclassesAccounts`.
        // Clear the local pending list so they don't appear in two places
        // when the next render runs. Idempotent injection in
        // `applyNewReclassesAccounts` protects us from races if the
        // re-emission happens before this clear.
        this.pendingNewReclassesAccounts = [];
        // REV-21798: the saved AR no longer carries the deleted rows, so the
        // session list has done its job.
        this.deletedReclassAccounts = [];
        // REV-DUPLICATE: uniqueAccountIds are now persisted in finStatements.
        this.pendingUniqueAccountIds = false;
    }

    private onSaveFailed(): void {
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }

    /**
     * Detects if grouping & masking data has changed compared to the original loaded data
     */
    private detectChangesInGroupingAndMasking(): boolean {
        // REV-20899: pending phantom reclasses accounts are stored on the
        // session list (`pendingNewReclassesAccounts`), not on
        // `IGroupingMasking`. A non-empty list means there are unsaved
        // phantoms that still need to land on `finStatements...fli.reclassesAccounts`,
        // so the AR is "dirty" even if every other field matches.
        if (this.pendingNewReclassesAccounts.length > 0) {
            return true;
        }

        // REV-21798: same reasoning for deletions — they only land in
        // `finStatements` at save time, so the AR is dirty until then.
        if (this.deletedReclassAccounts.length > 0) {
            return true;
        }

        if (this.originalGroupingAndMasking == null && this.groupingAndMaskingToBeSaved == null) {
            return false;
        }

        if (this.originalGroupingAndMasking == null && this.groupingAndMaskingToBeSaved != null) {
            return true;
        }

        if (this.originalGroupingAndMasking != null && this.groupingAndMaskingToBeSaved == null) {
            return true;
        }

        // Deep comparison of the data
        return !fastDeepEqual(this.originalGroupingAndMasking, this.groupingAndMaskingToBeSaved);
    }

    protected override isDataNewlyCreatedAndSaveable(): boolean {
        if (this.originalGroupingAndMasking == null && this.disableProceedButton === false) {
            return true;
        }
        return false;
    }

    /**
     * Override from AnnualReportSubPage to detect unsaved changes
     * This triggers the unsaved changes banner
     */
    protected override isDataChanged(): Observable<boolean> {
        if (this.isSaveRequestInProgress) {
            return this.savedSuccessfully.pipe(map((savedSuccessfully) => !savedSuccessfully));
        } else {
            return of(this.detectChangesInGroupingAndMasking());
        }
    }

    getAnnualReportToBeSaved(): IAnnualReport | undefined {
        if (this.annualReport == null || this.groupingAndMaskingToBeSaved == null) {
            return undefined;
        }

        // Check if data has actually changed
        const isDataChanged = this.detectChangesInGroupingAndMasking();

        // REV-20899: build the `finStatements` payload. Three concerns:
        //   1. Version bump if grouping/masking data changed (existing logic).
        //   2. Merge any session-pending phantom reclasses accounts into
        //      the matching FLI's `reclassesAccounts`. Phantoms must be
        //      written into a *deep clone* of the templates so we never
        //      mutate the NgRx store value held in `this.annualReport`.
        //   3. Prune any reclasses account (pending OR persisted) that is no
        //      longer referenced by any transaction, so deleted phantom rows
        //      are removed from the saved payload.
        //
        // After this save round-trips, the AR re-emits with the phantoms
        // already inside `finStatements`, and they behave like any other
        // reclasses account from that point on.
        const hasPendingPhantoms = this.pendingNewReclassesAccounts.length > 0;
        // REV-20899 semantics: include the session-pending phantoms so the save-path prune
        // cannot drop an account the user just created but has not referenced yet.
        const referencedKeys = GroupingMaskingComponent.buildReferencedReclassKeys(
            this.reclassTransactions,
            this.pendingNewReclassesAccounts,
        );
        // Also bump version when reclasses accounts were removed (orphan prune on save).
        const hasOrphanedReclassesToPrune =
            this.annualReport.finStatements.some((fs) =>
                GroupingHelper.hasOrphanedReclassesAccounts(
                    fs.templateAndAccountsMapping?.template?.group,
                    referencedKeys,
                ),
            );
        // REV-21798: composite keys of reclass accounts the user deleted this
        // session. They may still be referenced by incoming transactions, so
        // the orphan prune above would keep them — they need explicit removal.
        const deletedReclassKeys = new Set(
            this.deletedReclassAccounts.map((ref) => `${ref.accountNumber}|${ref.name ?? ''}`),
        );
        // REV-DUPLICATE: bump version when uniqueAccountIds were assigned.
        const shouldBumpVersion =
            isDataChanged ||
            hasPendingPhantoms ||
            hasOrphanedReclassesToPrune ||
            deletedReclassKeys.size > 0 ||
            this.pendingUniqueAccountIds;

        const updatedFinStatements = this.annualReport.finStatements.map((fs, index) => {
            // Spread to a new fs object first; we may need to also deep-clone
            // the template if we are about to inject phantoms or prune orphans.
            const next: IFinStatements = {
                ...fs,
                version: shouldBumpVersion ? (fs.version || 0) + 1 : (fs.version || 0),
            };

            if (fs.templateAndAccountsMapping?.template?.group) {
                const stmt = index === 0
                    ? ReclassificationStatement.BALANCE_SHEET
                    : ReclassificationStatement.INCOME_STATEMENT;
                const stmtHasPhantoms =
                    hasPendingPhantoms && this.pendingNewReclassesAccounts.some((p) => p.statement === stmt);

                // Clone the template once for both phantom injection and pruning.
                const clonedTemplate = GroupingMaskingComponent.deepClone(fs.templateAndAccountsMapping.template);

                if (stmtHasPhantoms) {
                    GroupingHelper.applyNewReclassesAccounts(
                        clonedTemplate.group,
                        this.pendingNewReclassesAccounts,
                        stmt,
                    );
                }

                // Always prune orphaned reclasses accounts from the save payload.
                GroupingHelper.pruneOrphanedReclassesAccounts(clonedTemplate.group, referencedKeys);

                // REV-21798: then drop the ones the user explicitly deleted.
                // Order matters only for clarity — the two sets are disjoint in
                // practice — but an explicit delete must win over any rule that
                // would otherwise retain the row.
                GroupingHelper.removeReclassesAccounts(clonedTemplate.group, deletedReclassKeys);

                next.templateAndAccountsMapping = {
                    ...fs.templateAndAccountsMapping,
                    template: clonedTemplate,
                };
            }
            return next;
        });

        const annualReportToBeSaved: IAnnualReport = {
            ...this.annualReport,
            groupingAndMasking: this.groupingAndMaskingToBeSaved,
            finStatements: updatedFinStatements,
        };

        return annualReportToBeSaved;
    }

    // --- ANNUAL REPORT SAVE OVERRIDES ---

    // --- COLLAPSE (IndexedDB Persistence) ---

    /**
     * Opens the IndexedDB connection. (Retained for collapse state)
     */
    private openDB(): Promise<IDBDatabase> {
        if (this.db) {
            return Promise.resolve(this.db);
        }

        return new Promise((resolve, reject) => {
            if (!window.indexedDB) {
                debug('IndexedDB not supported.');
                return reject(new Error('IndexedDB not supported'));
            }

            const request = window.indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                debug('Error opening IndexedDB:', request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };

            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                }
            };
        });
    }

    /**
     * Retrieves the collapse state from IndexedDB.
     */
    private loadCollapseStateFromIDB(): Promise<void> {
        if (!this.annualReportId) {
            return Promise.resolve();
        }

        return this.openDB().then((db) => {
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_NAME], 'readonly');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.get(this.annualReportId);

                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    const savedData = request.result;
                    if (savedData && savedData.collapse) {
                        this.collapseState = new Set(savedData.collapse);
                        debug('Collapse state loaded from IDB for AR ID:', this.annualReportId);
                    }
                    resolve();
                };
            });
        });
    }

    /**
     * Saves the current collapse state to IndexedDB.
     */
    private saveCollapseStateToIDB(): Promise<void> {
        if (!this.annualReportId) {
            return Promise.resolve();
        }

        // Only save the collapse state to IndexedDB
        const dataToSave = {
            id: this.annualReportId,
            collapse: Array.from(this.collapseState),
        };

        return this.openDB().then((db) => {
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_NAME], 'readwrite');
                const store = transaction.objectStore(STORE_NAME);

                // Use put to update or create
                const request = store.put(dataToSave);

                request.onerror = () => reject(request.error);
                request.onsuccess = () => resolve();
                transaction.oncomplete = () => resolve();
            });
        });
    }

    /**
     * Persists collapse state to IndexedDB. (Wrapper for saveCollapseStateToIDB)
     */
    private persistCollapseState(): void {
        this.saveCollapseStateToIDB().catch((error) => debug('Error persisting collapse state to IDB:', error));
    }

    // ===========================================
    // --- PRIVATE HELPERS (Non-Persistence) ---
    // ===========================================

    public static deepClone<T>(source: T): T {
        return GroupingMaskingHelper.deepClone(source);
    }

    /**
     * Recursively ensures all accounts arrays are initialized (never null/undefined)
     * This prevents template errors when iterating over accounts
     */
    public static ensureAccountsArrays(group: IAccountMappingTemplateGroup | any): void {
        GroupingMaskingHelper.ensureAccountsArrays(group);
    }

    /**
     * Ensures accounts arrays exist for Sub-FLIs within an accounts array
     */
    public static ensureSubFliAccountsArrays(accounts: any[]): void {
        GroupingMaskingHelper.ensureSubFliAccountsArrays(accounts);
    }

    /**
     * Recursively traverses the Group structure.
     * If it finds an item (FLI/SubFLI) that has a matching entry in sortingState,
     * it reorders the item's accounts to match that state.
     */
    public static applySavedSorting(group: IAccountMappingTemplateGroup | any, sortingState: Record<string, string[]>): void {
        GroupingMaskingHelper.applySavedSorting(group, sortingState);
    }

    public static applySortingToItem(item: any, sortingState: Record<string, string[]>): void {
        GroupingMaskingHelper.applySortingToItem(item, sortingState);
    }

    public static applySortingToSubFli(subFli: any, sortingState: Record<string, string[]>): void {
        GroupingMaskingHelper.applySortingToSubFli(subFli, sortingState);
    }

    // --- ACTION HANDLERS ---

    public onActionClicked(actionId: string): void {
        debug(`Action clicked: ${actionId}`);

        if (actionId === 'HIDE') {
            this.onHideSubtotals();
        } else if (actionId === 'UNHIDE') {
            this.onUnhideSubtotals();
        } else if (actionId === 'RENAME') {
            const allSelectedAccounts = this.getSelectedAccountObjects();

            // Create a unique list of accounts for the modal UI
            const uniqueAccounts = [];
            const seenIdentifiers = new Set();
            for (const account of allSelectedAccounts) {
                const identifier = this.getIdentifier(account);
                if (!seenIdentifiers.has(identifier)) {
                    seenIdentifiers.add(identifier);
                    uniqueAccounts.push(account);
                }
            }

            this.accountsToRename = uniqueAccounts;
            this.initializeRenameForm();
            this.openRenameModal = true;
        }
        if (actionId === 'GROUP') {
            const allSelectedAccounts = this.getSelectedAccountObjects();
            debug('GROUP action selected for accounts:', allSelectedAccounts);

            // Create a unique list of accounts for the modal UI
            const uniqueAccounts = [];
            const seenIdentifiers = new Set();
            for (const account of allSelectedAccounts) {
                const identifier = this.getIdentifier(account);
                if (!seenIdentifiers.has(identifier)) {
                    seenIdentifiers.add(identifier);
                    uniqueAccounts.push(account);
                }
            }
            // Implement grouping logic here
            this.accountsToGroup = uniqueAccounts;
            this.initializeGroupForm();
            this.openGroupModal = true;
            this.groupModalEditId = undefined;
        } else if (actionId === 'DELETE') {
            this.onDeleteSelectedReclassAccounts();
        } else if (actionId === "CLOSE_BULK_EDIT") {
            this.clearSelection();
        }
    }

    /**
     * REV-21798: delete the selected manual (reclassification) accounts.
     *
     * Guarded twice — the bulk bar disables the button for a mixed selection,
     * and this re-checks, since `onActionClicked` is reachable by id.
     *
     * When any of them carries reclassifications the user has to confirm
     * first; the actual work happens in `performReclassAccountDeletion`.
     */
    private onDeleteSelectedReclassAccounts(): void {
        const targets = Array.from(this.selectedAccounts.values()).filter((acc) =>
            this.isReclassAccountRow(acc as any),
        );
        if (targets.length === 0 || targets.length !== this.selectedAccounts.size) return;

        this.pendingReclassAccountDeletions = targets.map((acc) => this.reclassRefForAccount(acc as any));

        const hasTransactions = this.pendingReclassAccountDeletions.some((ref) =>
            (this.reclassTransactions ?? []).some((t) => refsEqual(t.source, ref) || refsEqual(t.target, ref)),
        );

        if (!hasTransactions) {
            this.performReclassAccountDeletion();
            return;
        }

        this.pendingConfirmAction = 'DELETE_RECLASS_ACCOUNTS';
        this.confirmText = this.i18nextService.t(
            'aumico_grouping_masking_reclass_delete_account_confirm_text',
        ) as string;
        this.confirmActionText = this.i18nextService.t(
            'aumico_grouping_masking_reclass_delete_account_confirm_action',
        ) as string;
        this.showConfirmDialog = true;
    }

    /**
     * REV-21798: actually remove the accounts queued in
     * `pendingReclassAccountDeletions`.
     *
     * Per account:
     *  - EVERY transaction touching it goes too, on either leg. A generated
     *    `MAN` account is almost always the TARGET (the source is the ordinary
     *    account that was reclassified), so deleting only the outgoing leg
     *    removed nothing and left the source still showing its reclass value —
     *    while the dialog had just promised "Konto & Umgliederungen löschen".
     *    Deleting both legs also means nothing is left dangling, so this no
     *    longer feeds the conflicts view.
     *  - it drops out of any G&M group (the group disappears when it empties,
     *    matching `onRemoveAccountFromGroup`);
     *  - session-pending entries are removed outright.
     *
     * Persisted rows are removed from the save payload by
     * `removeDeletedReclassAccounts` in `getAnnualReportToBeSaved` — orphan
     * pruning alone would not do it while an incoming transaction still
     * references them.
     */
    private performReclassAccountDeletion(): void {
        const refs = this.pendingReclassAccountDeletions;
        if (refs.length === 0) return;

        for (const ref of refs) {
            this.reclassStore.deleteByAccount(ref);

            this.pendingNewReclassesAccounts = this.pendingNewReclassesAccounts.filter(
                (p) => !(p.accountNumber === ref.accountNumber && p.name === ref.name),
            );

            const identifier = MaskingHelper.getAccountIdentifier(ref.accountNumber, ref.name, undefined);
            for (const groupId of Object.keys(this.groupedAccountsMap)) {
                const groupDef = this.groupedAccountsMap[groupId];
                if (!groupDef?.accounts?.includes(identifier)) continue;
                groupDef.accounts = groupDef.accounts.filter((a) => a !== identifier);
                if (groupDef.accounts.length === 0) delete this.groupedAccountsMap[groupId];
            }

            for (const containerId of Object.keys(this.sortingState)) {
                const order = this.sortingState[containerId];
                if (!Array.isArray(order) || !order.includes(identifier)) continue;
                this.sortingState[containerId] = order.filter((a) => a !== identifier);
            }

            this.deletedReclassAccounts.push({ ...ref });
        }

        this.pendingReclassAccountDeletions = [];
        this.clearSelection();
        this.persistGroupingAndMasking();
        this.renderTable();
    }

    private clearSelection(): void {
        this.selectedAccounts.clear();
        this.selectedSubtotals.clear();
        this.allAccountsSelected = false;
    }

    public onChangeRenaming(account: IAccount): void {
        this.accountsToRename = [account];
        this.initializeRenameForm();
        this.openRenameModal = true;
        this.editRenameModal = true;
    }

    public resetAccountName(
        accountNumber: string,
        originalName: string,
        shareholder?: string,
        uniqueAccountId?: string,
    ): void {
        // Drop BOTH forms: the row-scoped key this row may own, and the 2-part key an older save
        // used for every instance — resetting a name must not leave the old blanket rename behind.
        for (const identifier of MaskingHelper.getIdentifierCandidates({
            accountNumber,
            name: originalName,
            shareholder,
            uniqueAccountId,
        })) {
            delete this.maskedNamesMap[identifier];
        }
        this.openRenameModal = false;
        this.editRenameModal = false;
        this.selectedAccounts.clear();
    }

    private getSelectedAccountObjects(): IAccount[] | IGroupedAccount[] {
        // 1. Map.prototype.values() returns an iterable of the account objects (the values).
        // 2. Array.from() converts that iterable into an array.
        const selectedAccountObjects = Array.from(this.selectedAccounts.values());

        // You might need to cast the result if TypeScript is strict,
        // depending on your exact setup, but the type system should handle it.
        return selectedAccountObjects as IAccount[] | IGroupedAccount[];
    }

    private initializeRenameForm(): void {
        const formControls: { [key: string]: any } = {};
        this.accountsToRename.forEach((account) => {
            const identifier = this.getIdentifier(account);
            const existingMaskedName = this.maskedNamesMap[identifier] || '';
            // No longer required
            formControls[identifier] = [existingMaskedName];
        });
        this.renameForm = this.fb.group(formControls);
    }

    public onRenameSave(): void {
        if (this.renameForm.valid) {
            const newNames = this.renameForm.value;
            for (const identifier in newNames) {
                if (Object.prototype.hasOwnProperty.call(newNames, identifier)) {
                    const newName = newNames[identifier];
                    if (newName && newName.trim() !== '') {
                        this.maskedNamesMap[identifier] = newName.trim();
                    } else {
                        delete this.maskedNamesMap[identifier];
                    }
                }
            }
            this.persistGroupingAndMasking();
            this.renderTable();
            this.openRenameModal = false;
            this.selectedAccounts.clear();
        }
    }

    private initializeGroupForm(): void {
        const formControls: { [key: string]: any } = {};
        formControls['groupName'] = [''];
        this.groupForm = this.fb.group(formControls);
    }
    private initializeGroupEditForm(groupedAccount: IGroupedAccountDefition): void {
        const formControls: { [key: string]: any } = {};
        formControls['groupName'] = [groupedAccount.groupName];
        this.groupForm = this.fb.group(formControls);
    }

    public onGroupSave(): void {
        if (this.groupForm.valid && this.groupModalEditId === undefined) {
            const groupName = this.groupForm.value['groupName'];
            // Implement grouping logic here using this.accountsToGroup and groupName
            // For example, create a new group in the View Model and move selected accounts into it
            const groupId = this.accountsToGroup[0].sortingId + `__group__${this.sanitizeId(groupName)}`;
            const incoming = this.accountsToGroup.map((acc) => this.getIdentifier(acc));

            // REV-21798: MERGE into an existing definition instead of replacing it.
            //
            // The group id is derived from the container plus the group name, so grouping
            // anything under a name that already exists in that container lands on the SAME id.
            // Assigning a fresh definition there silently ejected every current member: they
            // stayed rendered (as ordinary rows under the FLI) but were no longer in the group,
            // so adding one account looked like the rest had vanished — and adding an account to
            // an existing group looked like it simply did not work.
            const existingAccounts = this.groupedAccountsMap[groupId]?.accounts ?? [];
            // An account may only ever belong to one group; drop it from any other first (same
            // rule `dropIntoGroup` applies). This can delete THIS group when it empties, which is
            // why its members were captured above. Scoped to each account's OWN container, so a row
            // that merely shares an identifier with one elsewhere (a balance-switch account's other
            // leg) keeps its grouping.
            for (const acc of this.accountsToGroup) {
                this.removeAccountFromAllGroups(
                    this.getIdentifier(acc),
                    undefined,
                    (acc as { sortingId?: string }).sortingId,
                );
            }
            const kept = existingAccounts.filter((identifier) => !incoming.includes(identifier));

            const groupedAccount: IGroupedAccountDefition = {
                groupName: groupName,
                groupId: groupId,
                accounts: [...kept, ...incoming],
            };

            this.groupedAccountsMap[groupId] = groupedAccount;

            // Render table first to apply grouping to view model, then persist
            // This ensures sortingState is regenerated with the groupId at the correct position
            this.renderTable();
            this.persistGroupingAndMasking();
            this.openGroupModal = false;
            this.groupModalEditId = undefined;
            this.selectedAccounts.clear();
        } else if (this.groupForm.valid && this.groupModalEditId !== undefined) {
            const groupName = this.groupForm.value['groupName'];
            // Implement grouping logic here using this.accountsToGroup and groupName
            // For example, update existing group in the View Model
            const groupedAccount = this.groupedAccountsMap[this.groupModalEditId];
            const groupId = groupedAccount['groupId'];
            if (this.groupedAccountsMap[groupId]) {
                const oldGroupName = this.groupedAccountsMap[groupId].groupName;
                const newGroupName = groupName;
                const oldSortingIdentifier = MaskingHelper.getAccountIdentifier(groupId, oldGroupName, '');
                const newSortingIdentifier = MaskingHelper.getAccountIdentifier(groupId, newGroupName, '');

                // Update sortingState keys
                const latestSortingState: { [containerId: string]: string[] } = this.sortingState;
                const updatedSortingState: { [containerId: string]: string[] } = {};
                // Loop through all keys and update any that match the old identifier
                for (const containerId in latestSortingState) {
                    updatedSortingState[containerId] = [...latestSortingState[containerId]];
                    for (let i = 0; i < latestSortingState[containerId].length; i++) {
                        if (latestSortingState[containerId][i] === oldSortingIdentifier) {
                            updatedSortingState[containerId][i] = newSortingIdentifier;
                        }
                    }

                }
                this.sortingState = updatedSortingState;

                this.groupedAccountsMap[groupId].groupName = newGroupName;
            }
            this.renderTable();
            this.persistGroupingAndMasking();
            this.openGroupModal = false;
            this.groupModalEditId = undefined;
            this.selectedAccounts.clear();
        }
    }

    public onEditGroup(groupId: string): void {
        // Create a unique list of accounts for the modal UI
        const groupedAccount = this.groupedAccountsMap[groupId];
        this.initializeGroupEditForm(groupedAccount);
        this.openGroupModal = true;
        this.groupModalEditId = groupId;
    }

    public onRemoveAccountFromGroup(account: IAccount, groupedAccount: IGroupedAccount): void {
        //get group account definition
        const groupDef = this.groupedAccountsMap[groupedAccount.groupId];
        if (groupDef) {
            //remove account from group definition
            // FIX: Use Identifier
            const identifier = this.getIdentifier(account);
            groupDef.accounts = groupDef.accounts.filter((accNum) => accNum !== identifier);

            //if no accounts left in group, remove the group
            if (groupDef.accounts.length === 0) {
                delete this.groupedAccountsMap[groupedAccount.groupId];
            }

            this.renderTable();
            this.persistGroupingAndMasking();
        }
    }

    public onUngroupAccounts(groupId: string): void {
        this.pendingConfirmAction = 'UNGROUP';
        if (this.groupedAccountsMap[groupId]) {
            this.confirmText = this.aumico_grouping_masking_confirm_ungroup_text(
                this.groupedAccountsMap[groupId].groupName,
            );
            this.showConfirmDialog = true;
            this.pendingUngroupId = groupId;
        }
    }

    public onCancelConfirm() {
        this.showConfirmDialog = false;
        this.confirmText = '';
        this.confirmActionText = this.aumico_grouping_masking_modal_confirm;
        this.pendingReclassAccountDeletions = [];
    }

    public onConfirm() {
        this.showConfirmDialog = false;
        this.confirmText = '';
        this.confirmActionText = this.aumico_grouping_masking_modal_confirm;

        if (this.pendingConfirmAction === 'DELETE_RECLASS_ACCOUNTS') {
            this.performReclassAccountDeletion();
            return;
        }

        if (this.pendingConfirmAction === 'UNGROUP' && this.groupedAccountsMap[this.pendingUngroupId]) {
            delete this.groupedAccountsMap[this.pendingUngroupId];
            this.renderTable();
            this.persistGroupingAndMasking();
        }

        if (this.pendingConfirmAction === 'UNSPLIT' && this.splitGroupAccountsMap[this.pendingUngroupId]) {
            delete this.splitGroupAccountsMap[this.pendingUngroupId];
            this.renderTable();
            this.persistGroupingAndMasking();
        }
    }

    public onDefineBalanceSwitchGroup(groupedAccount: IGroupedAccount, mainGroupIndex: number): void {
        const existingSplit = SplitAccountHelper.findSplitAccountById(
            this.splitGroupAccountsMap,
            groupedAccount.groupId,
        );
        if (existingSplit) {
            this.pendingConfirmAction = 'UNSPLIT';
            this.confirmText = this.aumico_grouping_masking_confirm_unsplitt_text(
                this.groupedAccountsMap[groupedAccount.groupId].groupName,
            );
            this.showConfirmDialog = true;
            this.pendingUngroupId = groupedAccount.groupId;
            return;
        }
        const accountToSplit = groupedAccount;

        if (accountToSplit) {
            this.accountToSplit = accountToSplit;
            const activaPreviewText = this.i18nextService.t('aumico_grouping_masking_assets', {
                lng: this.annualReport.locale,
            }) as string;
            const passivaPreviewText = this.i18nextService.t('aumico_grouping_masking_liabilities', {
                lng: this.annualReport.locale,
            }) as string;
            this.splitPreview = SplitAccountHelper.getSplit(
                this.accountToSplit,
                mainGroupIndex,
                activaPreviewText,
                passivaPreviewText,
            );

            const counterPartGroupIndex = mainGroupIndex === 0 ? 1 : 0;
            if (
                this.balanceSheet &&
                this.balanceSheet.sortedItems &&
                this.balanceSheet.sortedItems[counterPartGroupIndex]
            ) {
                const fliGroup = this.balanceSheet.sortedItems[counterPartGroupIndex] as IAccountMappingTemplateGroup;
                this.financialLineItems = SplitAccountHelper.getFinancialLineItems(fliGroup);
            }
            this.initializeSplitForm();
            this.openSplitModal = true;
        }
    }

    onRemoveSplitAccount(groupedAccount: IGroupedAccount): void {
        // remove _counterpart from groupId "__grp__Assets_0__fli__cash-and-cash-equivalents_0__group__XXX_counterpart"
        // should become "__grp__Assets_0__fli__cash-and-cash-equivalents_0__group__XXX"
        const splitAccountToRemove = groupedAccount.groupId.replace('_counterpart', '');
        this.pendingConfirmAction = 'UNSPLIT';

        this.confirmText = this.aumico_grouping_masking_confirm_unsplitt_text(
            this.groupedAccountsMap[splitAccountToRemove].groupName,
        );
        this.showConfirmDialog = true;
        this.pendingUngroupId = splitAccountToRemove;
        return;
    }

    private initializeSplitForm(fliName?: string): void {
        this.splitForm = this.fb.group({
            financialLineItem: [fliName || null, Validators.required],
        });
    }

    public onSplitAccountSave(): void {
        // 1. Guard Clause: Exit early if form is invalid
        if (!this.splitForm.valid) {
            return;
        }

        // 2. Retrieve and Validate the Financial Line Item
        const splitToFliId = this.splitForm.value['financialLineItem'];
        const fli = this.financialLineItems.find((f) => f.id === splitToFliId);

        if (!fli) {
            console.warn('Financial Line Item not found for ID:', splitToFliId);
            return;
        }

        // 3. Determine Context: Are we editing an existing split or creating a new one?
        // If splitModalEditId exists, we use it; otherwise, we default to the current account's group ID.
        const isEditMode = this.splitModalEditId !== undefined;
        const groupId = isEditMode ? this.splitModalEditId : this.accountToSplit.groupId;

        // 4. Update the Map
        this.splitGroupAccountsMap[groupId] = {
            counterpartFli: fli.originalItem.sortingId,
        };

        // 5. Persist and Cleanup
        this.persistGroupingAndMasking();
        this.renderTable();
        this.resetSplitModalState();
    }

    // Optional: Extract cleanup logic into a private helper for reusability
    private resetSplitModalState(): void {
        this.openSplitModal = false;
        this.splitModalEditId = undefined;
        this.selectedAccounts.clear();
    }

    // --- DISPLAY HELPERS ---

    /**
     * `uniqueAccountId` is what makes this row-aware: a TRUE DUPLICATE stores its rename under a
     * row-scoped key, so without it one instance would show the other's new name — or none at all.
     * Omitting it still resolves a rename saved under the plain 2-part key (every older report).
     */
    public getAccountDisplayName(
        accountNumber: string,
        originalName: string,
        shareholder?: string,
        uniqueAccountId?: string,
    ): string {
        const isShareholderAccount = shareholder != null && shareholder !== '';
        const displayName = isShareholderAccount
            ? `${originalName} - ${shareholder}`
            : `${originalName}`;
        // Attention: maskedNamesMap used to be keyed by accountNumber; it is now keyed by the composite identifier
        // returned from MaskingHelper.getAccountIdentifier (combining account number, name, and optional shareholder).
        return this.maskedNameFor(accountNumber, originalName, shareholder, uniqueAccountId) ?? displayName;
    }

    /**
     * The stored rename for one ROW, most specific key first (row-scoped, then the 2-part key that
     * covers every instance and is the only form older reports carry).
     */
    private maskedNameFor(
        accountNumber: string,
        originalName: string,
        shareholder?: string,
        uniqueAccountId?: string,
    ): string | undefined {
        const candidates = MaskingHelper.getIdentifierCandidates({
            accountNumber,
            name: originalName,
            shareholder,
            uniqueAccountId,
        });
        for (const identifier of candidates) {
            if (this.maskedNamesMap[identifier]) return this.maskedNamesMap[identifier];
        }
        return undefined;
    }
    public getAccountDisplayNameWithoutMasking(accountNumber: string, originalName: string, shareholder?: string): string {
        const isShareholderAccount = shareholder != null && shareholder !== '';
        const displayName = isShareholderAccount
            ? `${originalName} - ${shareholder}`
            : `${originalName}`;
        return displayName;
    }

    public isAccountMasked(
        accountNumber: string,
        originalName: string,
        shareholder?: string,
        uniqueAccountId?: string,
    ): boolean {
        return this.maskedNameFor(accountNumber, originalName, shareholder, uniqueAccountId) != null;
    }

    // --- SUBTOTAL HIDE/UNHIDE LOGIC ---

    public isSubtotalHidden(subtotalPath: string): boolean {
        const isHidden = this.hiddenSubtotalsSet.has(subtotalPath);
        return isHidden;
    }

    public getSubtotalPath(parentPath: string, subtotal: any): string {
        return GroupingPathHelper.getSubtotalPath(parentPath, subtotal);
    }

    public onToggleCheckSubtotal(subtotal: any, subtotalPath: string): void {
        const key = subtotalPath;

        if (!this.selectedSubtotals.has(key)) {
            // Clear account selection when selecting subtotal
            if (this.selectedAccounts.size > 0) {
                this.selectedAccounts.clear();
            }
            this.selectedSubtotals.set(key, subtotal);
        } else {
            this.selectedSubtotals.delete(key);
        }
    }

    private onHideSubtotals(): void {
        // Add all selected subtotals to hidden set
        this.selectedSubtotals.forEach((_, subtotalId) => {
            this.hiddenSubtotalsSet.add(subtotalId);
        });

        this.selectedSubtotals.clear();
        this.persistGroupingAndMasking();
        this.renderTable();
    }

    private onUnhideSubtotals(): void {
        // Remove all selected subtotals from hidden set
        this.selectedSubtotals.forEach((_, subtotalId) => {
            this.hiddenSubtotalsSet.delete(subtotalId);
        });

        this.selectedSubtotals.clear();
        this.persistGroupingAndMasking();
        this.renderTable();
    }

    // --- SELECTION HELPERS (FIX FOR CHECKBOXES) ---

    // Add this helper to ensure consistency
    private getSelectionKey(account: IAccount | IGroupedAccount): string {
        if ((account as any).groupId) {
            return (account as any).groupId;
        }
        // Use the same ID logic as getIdentifier for standard accounts
        return (account as any).sortingId + this.getIdentifier(account);
    }

    public onToggleCheckAccount(account: IAccount | IGroupedAccount): void {
        const key = this.getSelectionKey(account); // Use the helper

        if (!this.selectedAccounts.has(key)) {
            // Clear subtotal selection when selecting account
            if (this.selectedSubtotals.size > 0) {
                this.selectedSubtotals.clear();
            }

            // Check if there are other accounts selected
            if (this.selectedAccounts.size > 0) {
                // Get the first selected account
                const firstSelectedAccount = this.selectedAccounts.values().next().value;

                // Compare sortingId (which represents the FLI/Sub-FLI container)
                if (firstSelectedAccount['sortingId'] !== (account as any)['sortingId']) {
                    // If the new account is from a different FLI, clear the previous selection.
                    this.selectedAccounts.clear();
                }
            }
            this.selectedAccounts.set(key, account);
        } else {
            // If we are removing an account
            this.selectedAccounts.delete(key);
        }

        this.allAccountsSelected = false;
    }

    public onToggleCheckAccountClick(account: IAccount | IGroupedAccount, accounts: (IAccount | IGroupedAccount)[], event: MouseEvent) {
        if (event.shiftKey) {
            const values = Array.from(this.selectedAccounts.values());

            const lastSelectedAccount = values.length ? values[values.length - 1] : undefined;

            const lastSelectedAccountKey = this.getSelectionKey(lastSelectedAccount);
            const currentSelectedAccountKey = this.getSelectionKey(account);

            const currentSelectedAccountIndex = accounts.findIndex(acc => this.getSelectionKey(acc) === currentSelectedAccountKey);
            const lastSelectedAccountIndex = accounts.findIndex(acc => this.getSelectionKey(acc) === lastSelectedAccountKey);


            const start = Math.min(currentSelectedAccountIndex, lastSelectedAccountIndex);
            const end = Math.max(currentSelectedAccountIndex, lastSelectedAccountIndex);

            for (let i = start + 1; i <= end - 1; i++) {
                const key = this.getSelectionKey(accounts[i]);
                if (!this.selectedAccounts.has(key)) {
                    this.selectedAccounts.set(key, accounts[i]);
                }
            }

        }
    }

    // Create a public version for the HTML template
    public isAccountSelected(account: IAccount | IGroupedAccount): boolean {
        return this.selectedAccounts.has(this.getSelectionKey(account));
    }

    public onOpenAllFolders(): void {
        // Clear all collapsed states to expand all folders
        this.collapseState.clear();
        this.persistCollapseState();
        this.cdr.markForCheck();
        this.openAllFoldersWasClicked = true;
    }

    public onCloseAllFolders(): void {
        this.openAllFoldersWasClicked = false;

        // Collect all FLI and Sub-FLI paths and add them to collapseState
        if (this.balanceSheet) {
            this.collectAllFliPaths(this.balanceSheet, 'bs');
        }
        if (this.incomeStatement) {
            this.collectAllFliPaths(this.incomeStatement, 'is');
        }

        this.persistCollapseState();
        this.cdr.markForCheck();
    }

    /**
     * Recursively collects all FLI and Sub-FLI paths and adds them to collapseState
     */
    private collectAllFliPaths(group: IAccountMappingTemplateGroup | any, parentPath: string): void {
        if (!group) return;

        // Handle sortedItems array - look for FLIs
        if (group.sortedItems && Array.isArray(group.sortedItems)) {
            for (const item of group.sortedItems) {
                if (this.isGroup(item)) {
                    // Recursively handle nested groups
                    const groupPath = this.getGroupPath(parentPath, item);
                    this.collectAllFliPaths(item, groupPath);
                } else if (item.type === ItemType.FLI && this.hasFliContent(item)) {
                    // This is an FLI - add its path to collapse state
                    const fliPath = this.getFliPath(parentPath, item);
                    this.collapseState.add(fliPath);

                    // Also check for Sub-FLIs within this FLI's accounts
                    if (item.accounts && Array.isArray(item.accounts)) {
                        for (const acc of item.accounts) {
                            if (acc.type === 'SUB FLI' || acc.type === 'OF_THAT_ITEM') {
                                const subFliPath = this.getSubFliPath(fliPath, acc);
                                this.collapseState.add(subFliPath);
                            }
                        }
                    }
                }
            }
        }
    }

    public onSortButtonClick(event: Event): void {
        event.stopPropagation();

        if (!this.isSortMenuOpen) {
            // Calculate position based on button
            const target = event.target as HTMLElement;
            const button = target.closest('ham-button');

            if (button) {
                const rect = button.getBoundingClientRect();

                this.sortMenuPosition = {
                    top: rect.bottom + 8,
                    right: window.innerWidth - rect.right,
                    left: rect.right - 220, // Align right edge of flyout (220px width) to right edge of button
                };
            }
        }

        this.isSortMenuOpen = !this.isSortMenuOpen;
    }

    public closeSortMenu(): void {
        this.isSortMenuOpen = false;
    }

    public sortAccounts(sortType: 'alphabetical' | 'accountNumber'): void {
        this.closeSortMenu();

        // Sort both balance sheet and income statement
        if (this.balanceSheet) {
            this.sortAccountsInStructure(this.balanceSheet, sortType);
        }
        if (this.incomeStatement) {
            this.sortAccountsInStructure(this.incomeStatement, sortType);
        }

        // Persist the new sorting state
        this.persistGroupingAndMasking();
        this.cdr.markForCheck();
    }

    /**
     * Recursively sorts all accounts in the structure
     */
    private sortAccountsInStructure(
        group: IAccountMappingTemplateGroup | any,
        sortType: 'alphabetical' | 'accountNumber',
    ): void {
        if (!group) return;

        // Handle sortedItems array - look for FLIs
        if (group.sortedItems && Array.isArray(group.sortedItems)) {
            for (const item of group.sortedItems) {
                if (this.isGroup(item)) {
                    // Recursively handle nested groups
                    this.sortAccountsInStructure(item, sortType);
                } else if (item.type === ItemType.FLI && item.accounts && Array.isArray(item.accounts)) {
                    // Sort accounts in this FLI
                    this.sortAccountArray(item.accounts, sortType);

                    // Also sort accounts in Sub-FLIs
                    for (const acc of item.accounts) {
                        if (
                            (acc.type === 'SUB FLI' || acc.type === 'OF_THAT_ITEM') &&
                            acc.accounts &&
                            Array.isArray(acc.accounts)
                        ) {
                            this.sortAccountArray(acc.accounts, sortType);
                        }
                    }
                }
            }
        }
    }

    /**
     * Sorts an array of accounts based on the sort type.
     * Safely handles grouped accounts by falling back to groupId if accountNumber is missing.
     * REV-21342: structural rows (SubFLIs / Of-That items) are pinned at their mapping-defined
     * positions — the Sort button re-orders standard accounts only (incl. those INSIDE a SubFLI,
     * via the caller's recursion).
     */
    private sortAccountArray(accounts: any[], sortType: 'alphabetical' | 'accountNumber'): void {
        if (!accounts || accounts.length === 0) return;

        GroupingMaskingComponent.sortPreservingStructuralRows(accounts, (a, b) => {
            if (sortType === 'alphabetical') {
                // Sort by account name (considering masked names via component display logic)
                const accountDisplayNameA = this.getAccountDisplayName(a.accountNumber, a.name || '', a.shareholder, a.uniqueAccountId);
                const accountDisplayNameB = this.getAccountDisplayName(b.accountNumber, b.name || '', b.shareholder, b.uniqueAccountId);
                const nameA = accountDisplayNameA.toLowerCase();
                const nameB = accountDisplayNameB.toLowerCase();
                return nameA.localeCompare(nameB);
            } else {
                // Sort by account number
                // FIX: Check for groupId if accountNumber is missing (prevents breaking on grouped accounts)
                const numA = a.accountNumber || a.groupId || '';
                const numB = b.accountNumber || b.groupId || '';

                // Try numeric comparison first for cleaner ordering of standard numbers
                const numericA = parseInt(numA, 10);
                const numericB = parseInt(numB, 10);

                if (!isNaN(numericA) && !isNaN(numericB)) {
                    return numericA - numericB;
                }

                // Fallback to string comparison for alphanumeric IDs or complex group IDs
                return numA.localeCompare(numB);
            }
        });
    }

    // --- IMPORTANT: HELPER WRAPPER FOR HTML ---
    public getIdentifier(account: any): string {
        return GroupingMaskingComponent.accountIdentifierOf(account);
    }

    public static accountIdentifierOf(account: any): string {
        return GroupingMaskingHelper.accountIdentifierOf(account);
    }
}